import React, { useState, useRef, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Panel,
  PanelType,
  Dialog,
  DialogType,
  DialogFooter,
  PrimaryButton,
  DefaultButton,
  ContextualMenu,
  IContextualMenuProps,
  Icon,
  IconButton,
  TooltipHost,
  DirectionalHint
} from "@fluentui/react";
import "../styles/highlight-github.css";
import CopilotPanel from "./CopilotPanel";
import { ENDPOINTS } from "../Services/ServiceConfig";
import { logEvent } from "../helper/AppInsightsHelper";
import { ChatSession, ChatMessage } from "../interfaces/copilot-chat.interfaces";
import { copilotStreamingEnabled } from "../constants/FeatureConfig.constants";
import { streamCopilotResponse } from "../Utils/copilotStream";
import { appendThoughtToTimeline, applyStepToMessage } from "../Utils/copilotTimeline";
import {
  useGetCopilotAgentChatScenariosQuery,
  useSaveCopilotUserMessageReactionMutation,
  useSaveCopilotUserSessionFeedbackMutation
} from "../slices/AIHub.slice";
import { useSpeechRecognition } from "../components/AIPlayground/Components/CopilotChat/useSpeechRecognition";
import { useFileAttachment } from "../components/AIPlayground/Components/CopilotChat/useFileAttachment";
import ChatInput from "../components/AIPlayground/Components/CopilotChat/ChatInput";
import ChatMessageComponent from "../components/AIPlayground/Components/CopilotChat/ChatMessage";
import FeedbackCallout from "../components/AIPlayground/Components/CopilotChat/FeedbackCallout";
import ScenarioSelection from "../components/CopilotChat/ScenarioSelection";
import { ScenarioDetailsPanel } from "../components/CopilotChat/ScenarioDetailsPanel";
import { createWorker } from "tesseract.js";

const BASE_URL = `${ENDPOINTS.BASE_URL}aiplayground`;

const CopilotChat: React.FC = () => {
  const [searchParams] = useSearchParams();
  const sharedSessionIdFromUrl = searchParams.get("sessionId");
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(!!sharedSessionIdFromUrl); // Show loading immediately if we have a shared session ID
  const [showChat, setShowChat] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [renameSessionId, setRenameSessionId] = useState<string | null>(null);
  const [renameInput, setRenameInput] = useState("");
  const [contextMenuProps, setContextMenuProps] = useState<IContextualMenuProps | null>(null);
  const [expandedStates, setExpandedStates] = React.useState<{ [key: string]: boolean }>({});
  const [expandedScenarios, setExpandedScenarios] = React.useState<{ [key: string]: boolean }>({});
  const [isReadonlyMode, setIsReadonlyMode] = useState(false);
  const [isCopyingChat, setIsCopyingChat] = useState(false);
  const [readonlySessionOwner, setReadonlySessionOwner] = useState<string>("");
  const [readonlySession, setReadonlySession] = useState<ChatSession | null>(null);
  const [showCopyToast, setShowCopyToast] = useState(false);
  const [successToastMessage, setSuccessToastMessage] = useState<string>("Share link copied to clipboard!");
  const [errorToast, setErrorToast] = useState<string>("");
  const [isTogglingStar, setIsTogglingStar] = useState(false);
  const [needsNewChatAfterDelete, setNeedsNewChatAfterDelete] = useState(false);

  // Scenarios state - Initialize from URL if available
  const scenarioIdFromUrl = searchParams.get("scenarioId");
  const initialScenarioId = scenarioIdFromUrl ? parseInt(scenarioIdFromUrl, 10) : null;
  const [selectedScenario, setSelectedScenario] = useState<number | null>(initialScenarioId);
  const [shouldFetchScenarios, setShouldFetchScenarios] = useState(false);
  const [currentSessionScenario, setCurrentSessionScenario] = useState<{ scenarioId: number; scenarioName: string; scenarioGrouping?: string; state?: string } | null>(null);
  const streamAbortRef = useRef<AbortController | null>(null);
  const [showScenarioSelection, setShowScenarioSelection] = useState(!sharedSessionIdFromUrl); // Don't show scenario selection if we have a shared session ID
  const [isLoadingSharedSession, setIsLoadingSharedSession] = useState(!!sharedSessionIdFromUrl); // Track shared session loading
  const [isDetailsPanelOpen, setIsDetailsPanelOpen] = useState(false);
  const [isPromptsPanelOpen, setIsPromptsPanelOpen] = useState(false);
  const [showAllPrompts, setShowAllPrompts] = useState(false);

  // Feedback dialog state
  const [isFeedbackDialogOpen, setIsFeedbackDialogOpen] = useState(false);
  const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);
  const feedbackButtonRef = useRef<HTMLDivElement>(null);

  // RTK Query hook for fetching scenarios
  const {
    data: scenarios = [],
    isLoading: scenariosLoading
  } = useGetCopilotAgentChatScenariosQuery(undefined, {
    skip: !shouldFetchScenarios // Only fetch when shouldFetchScenarios is true
  });

  // RTK Query mutation hook for saving message reactions (like/dislike)
  const [saveMessageReaction] = useSaveCopilotUserMessageReactionMutation();

  // RTK Query mutation hook for saving session feedback (star rating + text)
  const [saveSessionFeedback] = useSaveCopilotUserSessionFeedbackMutation();

  // Parse scenario features for details panel
  const scenarioDetails = React.useMemo(() => {
    const features: { [key: number]: { supportedFeatures: string[]; unsupportedFeatures: string[] } } = {};
    scenarios.forEach((scenario: any) => {
      features[scenario.scenarioId] = {
        supportedFeatures: scenario.supportedFeatures
          ? scenario.supportedFeatures
            .split("|")
            .map((f: string) => f.trim())
            .filter((f: string) => f.length > 0)
          : [],
        unsupportedFeatures: scenario.unsupportedFeatures
          ? scenario.unsupportedFeatures
            .split("|")
            .map((f: string) => f.trim())
            .filter((f: string) => f.length > 0)
          : []
      };
    });
    return features;
  }, [scenarios]);

  // Get current scenario prompts
  const currentScenarioPrompts = React.useMemo(() => {
    if (!currentSessionScenario || !scenarios.length) return [];
    const scenario = scenarios.find((s: any) => s.scenarioId === currentSessionScenario.scenarioId);
    if (!scenario) return [];

    // Check suggestedPrompts field
    const prompts = (scenario as any).suggestedPrompts || "";
    if (prompts && prompts.length > 0) {
      return prompts
        .split("|")
        .map((p: string) => p.trim())
        .filter((p: string) => p.length > 0);
    }
    return [];
  }, [currentSessionScenario, scenarios]);

  // Toggle star status for the current session
  const handleToggleStar = async () => {
    if (!currentSessionId || currentSessionId === "" || isTogglingStar) return;

    const currentSession = sessions.find(s => s.id === currentSessionId);
    if (!currentSession) return;

    const newStarredState = !currentSession.isStarred;
    setIsTogglingStar(true);

    try {
      const response = await fetch(`${BASE_URL}/ToggleStarForChatSession`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${sessionStorage.getItem("token")}`
        },
        body: JSON.stringify({
          SessionId: currentSessionId,
          IsStarred: newStarredState
        })
      });

      if (response.ok) {
        // Update the session in the sessions array
        setSessions(prevSessions =>
          prevSessions.map(session =>
            session.id === currentSessionId
              ? { ...session, isStarred: newStarredState }
              : session
          )
        );

        logEvent("ToggleStarButton_Clicked", "AI Hub - Copilot Chat", "CopilotChat", {
          sessionId: currentSessionId,
          isStarred: newStarredState,
          scenarioId: currentSessionScenario?.scenarioId,
          scenarioName: currentSessionScenario?.scenarioName
        });
      } else {
        console.error("Failed to toggle star status");
        setErrorToast("Failed to toggle star status. Please try again.");
      }
    } catch (error) {
      console.error("Error toggling star status:", error);
      setErrorToast("Failed to toggle star status. Please try again.");
    } finally {
      setIsTogglingStar(false);
    }
  };

  // Use custom hooks
  const { isRecording, isSpeechSupported, transcript, startRecording, stopRecording } = useSpeechRecognition(input);
  const { attachedFile, attachedImages, handleFileChange, handleImageChange, removeFile, removeImageAt, setAttachedFile, setAttachedImages } = useFileAttachment();

  const chatContainerRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Helper function to get scenario grouping for a session
  const getSessionScenarioGrouping = React.useCallback((sessionScenarioId: number) => {
    if (sessionScenarioId === 0) return "General";
    const scenario = scenarios.find((s: any) => s.scenarioId === sessionScenarioId);
    return scenario?.scenarioGrouping || `Scenario ${sessionScenarioId}`;
  }, [scenarios]);

  // Helper function to toggle scenario expansion (accordion behavior - only one open at a time)
  const toggleScenarioExpansion = (scenarioGrouping: string) => {
    setExpandedScenarios(prev => {
      const isCurrentlyExpanded = prev[scenarioGrouping];
      // If clicking on already expanded item, collapse it; otherwise expand only this one
      if (isCurrentlyExpanded) {
        return { [scenarioGrouping]: false };
      } else {
        return { [scenarioGrouping]: true };
      }
    });
  };

  // Group sessions by scenario grouping with scenario metadata
  const sessionsByScenario = React.useMemo(() => {
    const grouped: { [key: string]: { sessions: ChatSession[], scenarioName: string, scenarioColor: string } } = {};

    // First, add all starred sessions to a "Starred" group
    const starredSessions = sessions.filter(session => session.isStarred);
    if (starredSessions.length > 0) {
      grouped["Starred"] = {
        sessions: [...starredSessions],
        scenarioName: "Starred",
        scenarioColor: "#FFB900"
      };
    }

    // Second, add the 5 most recent sessions to a "Recents" group
    const recentSessions = [...sessions]
      .filter(session => session.id !== "") // Filter out empty/new chat sessions
      .sort((a, b) => {
        const dateA = new Date(a.lastUpdated || "").getTime() || 0;
        const dateB = new Date(b.lastUpdated || "").getTime() || 0;
        return dateB - dateA; // Most recent first
      })
      .slice(0, 5); // Take top 5

    if (recentSessions.length > 0) {
      grouped["Recents"] = {
        sessions: recentSessions,
        scenarioName: "Recents",
        scenarioColor: "#0078D7"
      };
    }

    // Then, group all sessions (including starred ones) by their scenario grouping
    sessions.forEach(session => {
      const scenarioGrouping = getSessionScenarioGrouping(session.scenarioId ?? 0);

      if (!grouped[scenarioGrouping]) {
        grouped[scenarioGrouping] = {
          sessions: [],
          scenarioName: scenarioGrouping,
          scenarioColor: "#0078D7"
        };
      }

      grouped[scenarioGrouping].sessions.push(session);
    });

    // Sort sessions within each grouping by lastUpdated (newest first)
    Object.values(grouped).forEach(group => {
      group.sessions.sort((a, b) => {
        const dateA = new Date(a.lastUpdated || "").getTime() || 0;
        const dateB = new Date(b.lastUpdated || "").getTime() || 0;
        return dateB - dateA;
      });
    });

    return grouped;
  }, [sessions, getSessionScenarioGrouping]);

  const dotStyle: React.CSSProperties = {
    width: "6px",
    height: "6px",
    backgroundColor: "#000",
    borderRadius: "50%",
    display: "inline-block",
    margin: "0 2px",
    animation: "blink 1.4s infinite both"
  };

  // Handle scenario data when it's loaded
  useEffect(() => {
    if (scenarios.length > 0 && !scenariosLoading) {
      // Check if URL has scenarioId or scenarioGroup parameter - if so, don't set default scenario
      const hasScenarioIdInUrl = searchParams.get("scenarioId") !== null;
      const hasScenarioGroupInUrl = searchParams.get("scenarioGroup") !== null;

      // For new chats, set default scenario only if no scenario is currently selected AND no URL parameter
      if (currentSessionId === "" && selectedScenario === null && !hasScenarioIdInUrl && !hasScenarioGroupInUrl) {
        const defaultScenario = scenarios.find((s) => s.scenarioId === 0);
        if (defaultScenario) {
          setSelectedScenario(defaultScenario.scenarioId);
          // Replace \n with actual newlines for textarea
          const formattedDescription = defaultScenario.description.replace(/\\n/g, "\n");
          setInput(formattedDescription);
        } else if (scenarios.length > 0) {
          setSelectedScenario(scenarios[0].scenarioId);
          const formattedDescription = scenarios[0].description.replace(/\\n/g, "\n");
          setInput(formattedDescription);
        }

        // Auto-adjust textarea height after scenarios load
        setTimeout(() => {
          if (textareaRef.current) {
            textareaRef.current.style.height = "auto";
            textareaRef.current.style.height = textareaRef.current.scrollHeight + "px";
          }
        }, 100);
      } else {
        // For existing sessions, update scenario name if it's Loading... or Default
        const currentSession = sessions.find((s) => s.id === currentSessionId);
        if (
          currentSession &&
          currentSession.scenarioId !== undefined &&
          (!currentSessionScenario ||
            currentSessionScenario.scenarioName === "Default" ||
            currentSessionScenario.scenarioName === "Loading...")
        ) {
          const scenario = scenarios.find((s) => s.scenarioId === currentSession.scenarioId);
          if (scenario && scenario.scenarioName !== currentSessionScenario?.scenarioName) {
            setCurrentSessionScenario({
              scenarioId: currentSession.scenarioId,
              scenarioName: scenario.scenarioName,
              scenarioGrouping: (scenario as any).scenarioGrouping,
              state: scenario.state
            });
          }
        }

        // For readonly sessions, update scenario name if it's Loading...
        if (isReadonlyMode && readonlySession && readonlySession.scenarioId !== undefined &&
          currentSessionScenario?.scenarioName === "Loading...") {
          const scenario = scenarios.find(s => s.scenarioId === readonlySession.scenarioId);
          if (scenario) {
            setCurrentSessionScenario({
              scenarioId: readonlySession.scenarioId,
              scenarioName: scenario.scenarioName,
              scenarioGrouping: (scenario as any).scenarioGrouping,
              state: scenario.state
            });
          }
        }

        // Expand scenario group when scenarios are loaded and we have a current session
        if (currentSessionId && currentSessionId !== "") {
          const currentSession = sessions.find(s => s.id === currentSessionId);
          if (currentSession && currentSession.scenarioId !== undefined) {
            const selectedScenarioGrouping = getSessionScenarioGrouping(currentSession.scenarioId);
            // Only expand if we're not already showing the correct group
            setExpandedScenarios(prev => {
              if (!prev[selectedScenarioGrouping]) {
                return { [selectedScenarioGrouping]: true };
              }
              return prev;
            });
          }
        }
      }
    }
  }, [scenarios, scenariosLoading, currentSessionId, sessions, isReadonlyMode, readonlySession, currentSessionScenario]);

  // Fetch all sessions only once
  useEffect(() => {
    // Always fetch scenarios on initial load
    setShouldFetchScenarios(true);

    // Check URL parameters
    const sharedSessionId = searchParams.get("sessionId");
    const scenarioGroupParam = searchParams.get("scenarioGroup");
    const scenarioIdParam = searchParams.get("scenarioId");

    // Always fetch user's sessions
    fetch(`${BASE_URL}/GetUserChatSessions`, {
      headers: { Authorization: `Bearer ${sessionStorage.getItem("token")}` }
    })
      .then((res) => res.json())
      .then((data) => {
        const sorted = data
          .filter((s: any) => s.sessionId && s.sessionId !== "") // Filter out sessions with empty sessionId
          .sort((a: any, b: any) => new Date(b.lastUpdatedTime).getTime() - new Date(a.lastUpdatedTime).getTime())
          .map((s: any) => ({
            id: s.sessionId,
            title: s.sessionName,
            messages: [],
            lastUpdated: s.lastUpdatedTime,
            scenarioId: s.scenarioId, // Store scenarioId from API
            isStarred: !!(s.isStarred === 1 || s.isStarred === true || s.isStarred === "true") // Map IsStarred field from API (handle boolean, number, or string)
          }));

        if (sharedSessionId) {
          // Handle shared session - show dedicated loading state
          setSessions(sorted);
          setIsLoadingSharedSession(true);
          setLoading(true); // Show loading state while determining session ownership
          setShowChat(false); // Don't show new chat UI
          setShowScenarioSelection(false); // Don't show scenario selection
          loadSharedSession(sharedSessionId, sorted);
        } else if (scenarioGroupParam || scenarioIdParam) {
          // If URL has scenarioGroup or scenarioId, directly open NewChat with scenario selection
          // Create new chat session and add it to the top of existing sessions
          const newSession: ChatSession = {
            id: "",
            title: `New Chat ${sorted.length + 1}`,
            messages: [],
            lastUpdated: new Date().toISOString(),
            isStarred: false
          };
          setSessions([newSession, ...sorted]);
          setCurrentSessionId("");
          setMessages([]);
          setShowChat(false);
          setSelectedScenario(null);
          setCurrentSessionScenario(null);
          setShowScenarioSelection(true);
          setInput("");
          setAttachedFile(null);
          setAttachedImages([]);
          if (fileInputRef.current) fileInputRef.current.value = "";
        } else {
          // Normal mode: always show new chat with scenario selection
          setSessions(sorted);
          // Create new chat session with scenario selection by default
          const newSession: ChatSession = {
            id: "",
            title: `New Chat ${sorted.length + 1}`,
            messages: [],
            lastUpdated: new Date().toISOString(),
            isStarred: false
          };
          setSessions([newSession, ...sorted]);
          setCurrentSessionId("");
          setMessages([]);
          setShowChat(false);
          setSelectedScenario(null);
          setCurrentSessionScenario(null);
          setShowScenarioSelection(true);
          setInput("");
          setAttachedFile(null);
          setAttachedImages([]);
          if (fileInputRef.current) fileInputRef.current.value = "";
        }
      })
      .catch(console.error);
  }, [searchParams]);

  // Sync transcript from speech recognition to input
  useEffect(() => {
    if (transcript) {
      setInput(transcript);
      // Auto-adjust textarea height after transcript update
      if (textareaRef.current) {
        textareaRef.current.style.height = "auto";
        textareaRef.current.style.height = textareaRef.current.scrollHeight + "px";
      }
    }
  }, [transcript]);

  const loadSharedSession = async (sessionId: string, sortedSessions?: ChatSession[]) => {
    setMessages([]);
    setShowChat(false);
    setLoading(true);
    setInput("");
    setAttachedFile(null);
    setAttachedImages([]);

    setCurrentSessionScenario(null);

    if (fileInputRef.current) fileInputRef.current.value = "";

    try {
      const res = await fetch(`${BASE_URL}/GetCopilotChatSession?sessionId=${sessionId}`, {
        headers: { Authorization: `Bearer ${sessionStorage.getItem("token")}` }
      });
      const data = await res.json();

      // Check if session exists (sessionId is empty if not found)
      if (!data.sessionId || data.sessionId === "") {
        // Session not found - show error toast
        logEvent("SharedChatLink_SessionNotFound", "AI Hub - Copilot Chat", "CopilotChat", {
          sessionId: sessionId
        });
        setErrorToast("Session not found. The shared link is invalid or the session has been deleted.");
        setTimeout(() => setErrorToast(""), 5000);

        // Exit readonly mode and load user's last session
        setIsReadonlyMode(false);
        setReadonlySession(null);
        setReadonlySessionOwner("");

        // Update URL to remove invalid sessionId parameter
        window.history.pushState({}, "", "/aihub");

        // Load user's most recent session (already sorted by lastUpdatedTime)
        const sessionsToUse = sortedSessions || sessions;
        if (sessionsToUse.length > 0) {
          loadSessionHistory(sessionsToUse[0].id);
          setCurrentSessionId(sessionsToUse[0].id);
        } else {
          setShowChat(false);
          setCurrentSessionId("");
        }
        return;
      }

      // Log successful shared link opening
      logEvent("SharedChatLink_Opened", "AI Hub - Copilot Chat", "CopilotChat", {
        sessionId: sessionId,
        sessionName: data.sessionName,
        scenarioId: data.scenarioId,
        sharedBy: data.loginEmail
      });

      // Check if the session belongs to the current user
      const currentUserEmail = sessionStorage.getItem("loginUserName")?.toString();
      const sessionBelongsToCurrentUser = currentUserEmail && currentUserEmail === data.loginEmail;

      if (sessionBelongsToCurrentUser) {
        // If session belongs to current user, handle it as a normal session
        // Exit readonly mode and load the session normally
        setIsReadonlyMode(false);
        setReadonlySession(null);
        setReadonlySessionOwner("");

        // Update URL to remove sessionId parameter since we're treating it as a normal session
        window.history.pushState({}, "", "/aihub");

        // Find the session in user's sessions (already fetched) and load it
        const sessionsToUse = sortedSessions || sessions;
        const existingSession = sessionsToUse.find(s => s.id === sessionId);

        if (existingSession) {
          // Session exists in user's sessions, load it normally
          setCurrentSessionId(sessionId);

          // Expand the correct scenario group
          const selectedScenarioGrouping = getSessionScenarioGrouping(existingSession.scenarioId ?? 0);
          setExpandedScenarios({ [selectedScenarioGrouping]: true });

          // Don't show scenario selection for user's own session
          setShowScenarioSelection(false);
          setShowChat(true); // Ensure chat is visible
          setIsLoadingSharedSession(false); // Stop shared session loading

          loadSessionHistory(sessionId);
        } else {
          // Session might not be in the list yet (could be very new),
          // but since it belongs to the user, we can still load it
          // Add it to the sessions list and load it
          const userSession: ChatSession = {
            id: data.sessionId,
            title: data.sessionName,
            messages: [],
            lastUpdated: data.lastUpdatedTime,
            scenarioId: data.scenarioId,
            isStarred: !!(data.isStarred === 1 || data.isStarred === true || data.isStarred === "true")
          };

          const updatedSessions = [userSession, ...sessionsToUse];
          setSessions(updatedSessions);
          setCurrentSessionId(sessionId);

          // Expand the correct scenario group
          const selectedScenarioGrouping = getSessionScenarioGrouping(data.scenarioId ?? 0);
          setExpandedScenarios({ [selectedScenarioGrouping]: true });

          // Don't show scenario selection for user's own session
          setShowScenarioSelection(false);
          setShowChat(true); // Ensure chat is visible
          setIsLoadingSharedSession(false); // Stop shared session loading

          loadSessionHistory(sessionId);
        }
        return; // Exit early since we're handling it as a normal session
      }

      // Store session owner and readonly session (only for sessions not belonging to current user)
      setReadonlySessionOwner(data.loginEmail);
      setIsReadonlyMode(true);
      setCurrentSessionId(sessionId);

      // Create session entry for readonly section in sidebar
      const sharedSession: ChatSession = {
        id: data.sessionId,
        title: data.sessionName,
        messages: [],
        lastUpdated: data.lastUpdatedTime,
        scenarioId: data.scenarioId,
        isStarred: !!(data.isStarred === 1 || data.isStarred === true || data.isStarred === "true")
      };
      setReadonlySession(sharedSession);

      // Map messages - using same logic as loadSessionHistory
      const msgs = data.messages.map((m: any) => {
        let messageText = m.text;
        let hasAttachment = false;
        let attachmentData: any = undefined;

        // For user messages, check if it's JSON and extract userPrompt
        if (m.role === "user") {
          try {
            const parsedMessage = JSON.parse(m.text);
            if (parsedMessage.userPrompt) {
              messageText = parsedMessage.userPrompt;
              hasAttachment = !!(parsedMessage.description || parsedMessage.groundingInstructions);
              if (hasAttachment) {
                attachmentData = {
                  description: parsedMessage.description,
                  groundingInstructions: parsedMessage.groundingInstructions,
                  originalText: m.text
                };
              }
            }
          } catch (e) {
            // If parsing fails, use original text
            messageText = m.text;
          }
        }

        return {
          id: m.id, // Message ID from API for reaction tracking
          role: m.role === "user" ? "user" : "ai",
          text: messageText,
          images: m.images || [],
          content: m.content,
          functionCalls: m.functionCalls,
          action: m.action, // Reaction state: true=liked, false=disliked, null=no reaction
          hasAttachment,
          attachmentData
        };
      });
      setMessages(msgs);
      setShowChat(msgs.length > 0);
      setShowScenarioSelection(false); // Don't show scenario selection for readonly sessions
      setLoading(false); // Stop loading for readonly sessions
      setIsLoadingSharedSession(false); // Stop shared session loading

      // Set scenario information
      if (msgs.length > 0 && data.scenarioId !== undefined) {
        if (scenarios.length > 0) {
          // If scenarios loaded, find the scenario name by ID
          const scenario = scenarios.find((s) => s.scenarioId === data.scenarioId);
          if (scenario) {
            setCurrentSessionScenario({
              scenarioId: scenario.scenarioId,
              scenarioName: scenario.scenarioName,
              scenarioGrouping: (scenario as any).scenarioGrouping,
              state: scenario.state
            });
          }
        } else {
          // If scenarios not loaded yet, show placeholder
          setCurrentSessionScenario({
            scenarioId: data.scenarioId,
            scenarioName: "Loading...",
            state: ""
          });
        }
      }
    } catch (err) {
      console.error(err);
      // Show error toast
      setErrorToast("Failed to load shared session. An error occurred while loading the session.");
      setTimeout(() => setErrorToast(""), 5000);

      // Exit readonly mode and load user's last session
      setIsReadonlyMode(false);
      setReadonlySession(null);
      setReadonlySessionOwner("");
      setLoading(false); // Stop loading on error
      setIsLoadingSharedSession(false); // Stop shared session loading on error

      // Update URL to remove sessionId parameter
      window.history.pushState({}, "", "/aihub");

      // Load user's most recent session (already sorted by lastUpdatedTime)
      const sessionsToUse = sortedSessions || sessions;
      if (sessionsToUse.length > 0) {
        loadSessionHistory(sessionsToUse[0].id);
        setCurrentSessionId(sessionsToUse[0].id);
      } else {
        setShowChat(false);
        setCurrentSessionId("");
      }
    } finally {
      setLoading(false);
      if (chatContainerRef.current) {
        chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
      }
    }
  };

  const loadSessionHistory = async (sessionId: string) => {
    setMessages([]);
    setLoading(true);
    setInput("");
    setAttachedFile(null);
    setAttachedImages([]);
    // Close prompts panel when changing sessions
    setIsPromptsPanelOpen(false);
    // Pre-populate scenario while loading based on session's scenarioId
    const preSession = sessions.find(s => s.id === sessionId);
    if (preSession && preSession.scenarioId !== undefined) {
      const scenarioFromList = scenarios.find(s => s.scenarioId === preSession.scenarioId);
      setCurrentSessionScenario({
        scenarioId: preSession.scenarioId,
        scenarioName: scenarioFromList ? scenarioFromList.scenarioName : "Loading...",
        scenarioGrouping: scenarioFromList ? (scenarioFromList as any).scenarioGrouping : undefined,
        state: scenarioFromList ? scenarioFromList.state : ""
      });
    } else {
      setCurrentSessionScenario({ scenarioId: 0, scenarioName: "Loading...", state: "" });
    }
    if (fileInputRef.current) fileInputRef.current.value = "";
    try {
      const res = await fetch(`${BASE_URL}/GetChatSessionHistory?sessionId=${sessionId}`, {
        headers: { Authorization: `Bearer ${sessionStorage.getItem("token")}` },

      });
      const data = await res.json();
      const msgs = data.map((m: any) => {
        let messageText = m.text;
        let hasAttachment = false;
        let attachmentData: any = undefined;

        // For user messages, check if it's JSON and extract userPrompt
        if (m.role === "user") {
          try {
            const parsedMessage = JSON.parse(m.text);
            if (parsedMessage.userPrompt) {
              messageText = parsedMessage.userPrompt;
              hasAttachment = !!(parsedMessage.description || parsedMessage.groundingInstructions);
              if (hasAttachment) {
                attachmentData = {
                  description: parsedMessage.description,
                  groundingInstructions: parsedMessage.groundingInstructions,
                  originalText: m.text
                };
              }
            }
          } catch (e) {
            // If parsing fails, use original text
            messageText = m.text;
          }
        }

        return {
          id: m.id, // Message ID from API for reaction tracking
          role: m.role === "user" ? "user" : "ai",
          text: messageText,
          images: m.images || [],
          content: m.content,
          functionCalls: m.functionCalls,
          action: m.action, // Reaction state: true=liked, false=disliked, null=no reaction
          hasAttachment,
          attachmentData
        };
      });
      setMessages(msgs);
      // Keep chat visible during session switch to avoid flicker
      setShowChat(true);

      // Scenario name will be updated by useEffect when scenarios are available
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
      if (chatContainerRef.current) {
        chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
      }
    }
  };

  const handleNewChat = (initialLoad: boolean = false) => {
    // Track New Chat button click (only for user-initiated clicks, not initial load)
    if (!initialLoad) {
      logEvent("NewChatButton_Clicked", "AI Hub - Copilot Chat", "CopilotChat", {});
    }

    const cleanedSessions = sessions.filter((s) => !(s.id === "" && s.messages.length === 0));
    const newSession: ChatSession = {
      id: "",
      title: initialLoad ? "" : `New Chat ${cleanedSessions.length + 1}`,
      messages: [],
      lastUpdated: new Date().toISOString(),
      isStarred: false
    };
    setSessions([newSession, ...cleanedSessions]);
    setCurrentSessionId("");
    setMessages([]);
    setShowChat(false);
    setSelectedScenario(null);
    setCurrentSessionScenario(null);
    if (!initialLoad) {
      setShowScenarioSelection(true); // Show scenario selection only for user-initiated new chat
    }

    // Clear readonly mode and session when creating new chat
    if (isReadonlyMode || readonlySession) {
      setIsReadonlyMode(false);
      setReadonlySession(null);
      setReadonlySessionOwner("");
    }

    // Clear URL parameters when starting new chat
    const url = new URL(window.location.href);
    url.searchParams.delete("scenarioGroup");
    url.searchParams.delete("scenarioId");
    url.searchParams.delete("sessionId");
    window.history.replaceState({}, "", url.toString());

    //  Clear input and attached file
    setInput("");
    setAttachedFile(null);
    setAttachedImages([]);
    if (fileInputRef.current) fileInputRef.current.value = "";

    // Fetch scenarios when creating a new chat
    setShouldFetchScenarios(true);
  };

  // Run only once after component mounts - removed to prevent creating empty session
  // useEffect(() => {
  //     if (sessionsLoaded && sessions.length === 0) {
  //         handleNewChat(true);
  //     }
  // }, [sessionsLoaded, sessions.length]);

  // Handle creating a new chat when all sessions are deleted
  useEffect(() => {
    if (needsNewChatAfterDelete && sessions.length === 0) {
      setNeedsNewChatAfterDelete(false);
      handleNewChat(false);
    }
  }, [needsNewChatAfterDelete, sessions.length]);

  // Helper to produce a standard in-thread error AI message
  const makeErrorMessage = (retryUserText: string): ChatMessage => ({
    role: "ai",
    text: "",
    isError: true,
    retryUserText
  });

  // Retry a previously failed message. Removes the error AI message at errorIndex
  // and re-sends the original user text with grounding that tells the agent this is a retry.
  const handleRetry = async (retryUserText: string, errorMessageIndex: number) => {
    if (loading) return;

    // Remove the error message and set loading
    setMessages((prev) => prev.filter((_, i) => i !== errorMessageIndex));
    setLoading(true);

    const userId = sessionStorage.getItem("userId") || "unknown";

    logEvent("UserMessage_Retry", "AI Hub - Copilot Chat", "CopilotChat", {
      userId,
      sessionId: currentSessionId,
      scenarioId: currentSessionScenario?.scenarioId
    });

    const retryMessageJson = JSON.stringify({
      description: "Retry the previous message",
      groundingInstructions: "User has sent this message previously, now the user is retrying it because some error happened",
      userPrompt: retryUserText
    });

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 300000);

      let response;
      try {
        if (!currentSessionId || currentSessionId === "") {
          const selectedScenarioData = scenarios.find((s) => s.scenarioId === selectedScenario);
          const scenarioId = selectedScenario !== null ? selectedScenario : 0;
          response = await fetch(`${BASE_URL}/InitiateCopilotChatSession`, {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${sessionStorage.getItem("token")}` },
            body: JSON.stringify({
              SessionId: "",
              Message: retryMessageJson,
              ScenarioId: scenarioId,
              ScenarioName: selectedScenarioData?.scenarioName || "Default"
            }),
            signal: controller.signal
          });
        } else {
          response = await fetch(`${BASE_URL}/GetChatSessionMessageResponse`, {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${sessionStorage.getItem("token")}` },
            body: JSON.stringify({
              SessionId: currentSessionId,
              Message: retryMessageJson,
              ScenarioId: currentSessionScenario?.scenarioId,
              ScenarioName: currentSessionScenario?.scenarioName
            }),
            signal: controller.signal
          });
        }
      } finally {
        clearTimeout(timeoutId);
      }

      if (!response.ok) {
        throw new Error(`API returned status ${response.status}`);
      }

      const data = await response.json();

      if (!data.messages || data.messages.length === 0) {
        throw new Error("Empty response from API");
      }

      const newMessages: ChatMessage[] = data.messages.map((m: any) => ({
        id: m.id,
        role: m.role === "user" ? "user" : "ai",
        text: m.text,
        images: m.images || [],
        content: m.content,
        functionCalls: m.functionCalls || [],
        action: m.action ?? null
      }));

      const hasAgentError = newMessages.some((msg) => msg.text?.includes("AI Agent Response Error"));
      if (hasAgentError) {
        throw new Error("Agent returned an error response");
      }

      setMessages((prev) => [...prev, ...newMessages]);

      // Update currentSessionId and session list if a new session was initiated
      if (!currentSessionId && data.sessionId) {
        setCurrentSessionId(data.sessionId);
        setSessions((prev) => [
          {
            id: data.sessionId,
            title: "Retry Chat",
            messages: [],
            lastUpdated: new Date().toISOString(),
            scenarioId: selectedScenario ?? undefined,
            isStarred: false
          },
          ...prev
        ]);
      }
    } catch (err: any) {
      logEvent("CopilotChat_Failure", "AI Hub - Copilot Chat", "CopilotChat", {
        userId,
        sessionId: currentSessionId,
        scenarioId: currentSessionScenario?.scenarioId,
        failureType: "RETRY_FAILURE",
        errorMessage: err.message || "Unknown error"
      });
      setMessages((prev) => [...prev, makeErrorMessage(retryUserText)]);
    } finally {
      setLoading(false);
      if (chatContainerRef.current) {
        chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
      }
    }
  };

  // ---- Streaming (SSE) helpers ----
  const updateStreamingMessage = (updater: (m: ChatMessage) => ChatMessage) => {
    setMessages((prev) => {
      for (let i = prev.length - 1; i >= 0; i--) {
        if (prev[i].isStreaming) {
          const copy = [...prev];
          copy[i] = updater(copy[i]);
          return copy;
        }
      }
      return prev;
    });
  };

  const renameStreamingSession = async (sessionId: string, userInputText: string, previousSessionId: string) => {
    let newTitle = userInputText;
    if (userInputText.length > 40) {
      const words = userInputText.split(" ");
      let truncated = "";
      for (const word of words) {
        const potentialLength = truncated ? truncated.length + 1 + word.length : word.length;
        if (potentialLength + 3 <= 40) {
          truncated += (truncated ? " " : "") + word;
        } else {
          break;
        }
      }
      newTitle = truncated + "...";
    }
    try {
      await fetch(`${BASE_URL}/RenameChatSession`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${sessionStorage.getItem("token")}`
        },
        body: JSON.stringify({ sessionId, sessionName: newTitle })
      });
    } catch (err) {
      console.error("Failed to update session name:", err);
    }
    setSessions((prev) =>
      prev.map((s) =>
        s.id === "" || s.id === previousSessionId || s.id === sessionId
          ? { ...s, id: sessionId, title: newTitle, scenarioId: selectedScenario !== null ? selectedScenario : s.scenarioId, lastUpdated: new Date().toISOString() }
          : s
      )
    );
  };

  const runStreamingSend = async (messageContent: string, userInputText: string, userId: string) => {
    const isFirst = currentSessionId === "" && selectedScenario !== null;
    const previousSessionId = currentSessionId ?? "";
    let url: string;
    let body: any;

    if (isFirst) {
      const selectedScenarioData = scenarios.find((s) => s.scenarioId === selectedScenario);
      const firstMessageJson = {
        description: selectedScenarioData?.description || "Scenario Description",
        groundingInstructions: (selectedScenarioData as any)?.groundingInstructions || "",
        userPrompt: messageContent
      };
      url = `${BASE_URL}/InitiateCopilotChatSessionStream`;
      body = { SessionId: "", Message: JSON.stringify(firstMessageJson), ScenarioId: selectedScenario, ScenarioName: selectedScenarioData?.scenarioName || "Default" };
    } else {
      url = `${BASE_URL}/GetChatSessionMessageResponseStream`;
      body = { SessionId: currentSessionId, Message: messageContent, ScenarioId: currentSessionScenario?.scenarioId, ScenarioName: currentSessionScenario?.scenarioName };
    }

    setMessages((prev) => [...prev, { role: "ai", text: "", functionCalls: [], timeline: [], isStreaming: true }]);

    const controller = new AbortController();
    streamAbortRef.current = controller;
    const token = sessionStorage.getItem("token");
    const streamStart = Date.now();
    let firstTokenLogged = false;
    let stepCount = 0;

    try {
      await streamCopilotResponse(url, body, token, {
        onMeta: (sid) => {
          if (sid && isFirst) {
            setCurrentSessionId(sid);
            if (selectedScenario !== null) {
              const scenario = scenarios.find((s) => s.scenarioId === selectedScenario);
              setCurrentSessionScenario({
                scenarioId: selectedScenario,
                scenarioName: scenario ? scenario.scenarioName : "Default",
                scenarioGrouping: scenario ? (scenario as any).scenarioGrouping : undefined,
                state: scenario ? scenario.state : ""
              });
            }
            renameStreamingSession(sid, userInputText, previousSessionId);
          }
        },
        onToken: (tok) => {
          if (!firstTokenLogged) {
            firstTokenLogged = true;
            logEvent("FirstToken_Received", "AI Hub - Copilot Chat", "CopilotChat", { userId, ttftMs: Date.now() - streamStart });
          }
          updateStreamingMessage((m) => ({ ...m, text: m.text + tok }));
        },
        onStep: (step) => {
          stepCount++;
          logEvent("ToolStep", "AI Hub - Copilot Chat", "CopilotChat", { userId, name: step.name, status: step.status });
          updateStreamingMessage((m) => applyStepToMessage(m, step));
        },
        onThought: (thought, source) => {
          updateStreamingMessage((m) => ({
            ...m,
            thoughts: (m.thoughts || "") + thought,
            timeline: appendThoughtToTimeline(m.timeline, thought, source),
          }));
        },
        onDone: (msgs) => {
          const mapped: ChatMessage[] = (msgs || []).map((m: any) => ({
            id: m.id,
            role: m.role === "user" ? "user" : "ai",
            text: m.text,
            images: m.images || [],
            content: m.content,
            functionCalls: m.functionCalls || [],
            action: m.action ?? null
          }));
          setMessages((prev) => {
            const idx = prev.findIndex((mm) => mm.isStreaming);
            if (idx < 0) return [...prev, ...mapped];
            const priorThoughts = prev[idx].thoughts;
            const priorTimeline = prev[idx].timeline;
            const replacement = mapped.length ? [...mapped] : [{ ...prev[idx], isStreaming: false }];
            // Attach the streamed thoughts/timeline to the AI message that represents the
            // response (the last AI message in the done payload), not always the first.
            let targetIndex = -1;
            for (let j = replacement.length - 1; j >= 0; j--) {
              if (replacement[j].role === "ai") {
                targetIndex = j;
                break;
              }
            }
            if (targetIndex === -1 && replacement.length) targetIndex = 0;
            if (targetIndex !== -1) {
              replacement[targetIndex] = { ...replacement[targetIndex], thoughts: priorThoughts, timeline: priorTimeline };
            }
            return [...prev.slice(0, idx), ...replacement, ...prev.slice(idx + 1)];
          });
          logEvent("Stream_Completed", "AI Hub - Copilot Chat", "CopilotChat", { userId, totalMs: Date.now() - streamStart, stepCount });
        },
        onError: (err) => {
          setMessages((prev) => {
            const idx = prev.findIndex((mm) => mm.isStreaming);
            if (idx < 0) return prev;
            return [...prev.slice(0, idx), makeErrorMessage(userInputText), ...prev.slice(idx + 1)];
          });
          logEvent("CopilotChat_Failure", "AI Hub - Copilot Chat", "CopilotChat", { userId, sessionId: currentSessionId, failureType: "STREAM_ERROR", errorMessage: err });
        }
      }, controller.signal);
    } catch (e: any) {
      if (controller.signal.aborted) {
        updateStreamingMessage((m) => ({ ...m, isStreaming: false, isStopped: true }));
        logEvent("Stream_Cancelled", "AI Hub - Copilot Chat", "CopilotChat", { userId, sessionId: currentSessionId });
      } else {
        setMessages((prev) => {
          const idx = prev.findIndex((mm) => mm.isStreaming);
          if (idx < 0) return prev;
          return [...prev.slice(0, idx), makeErrorMessage(userInputText), ...prev.slice(idx + 1)];
        });
        logEvent("CopilotChat_Failure", "AI Hub - Copilot Chat", "CopilotChat", { userId, sessionId: currentSessionId, failureType: "STREAM_EXCEPTION", errorMessage: e?.message || "stream_error" });
      }
    } finally {
      streamAbortRef.current = null;
      setLoading(false);
      setAttachedFile(null);
      setAttachedImages([]);
      if (chatContainerRef.current) {
        chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
      }
    }
  };

  const handleSend = async () => {
    if (!input.trim() && !attachedFile && attachedImages.length === 0) return;

    if (!showChat) setShowChat(true);

    // Hide scenario selection when user sends first message
    setShowScenarioSelection(false);

    // Get the user input text (what will be displayed)
    const userInputText = input.trim()
      || (attachedFile
        ? attachedFile.name
        : attachedImages.length > 0
          ? `${attachedImages[0].name}${attachedImages.length > 1 ? ` (+${attachedImages.length - 1} more)` : ""}`
          : "");

    // Check if this is a JSON message with attachments (first message case)
    const isFirstMessage = currentSessionId === "" && selectedScenario !== null;
    let hasAttachment = false;
    let attachmentData: any = undefined;

    if (isFirstMessage) {
      const selectedScenarioData = scenarios.find(s => s.scenarioId === selectedScenario);
      hasAttachment = true;
      attachmentData = {
        description: selectedScenarioData?.description || "Scenario Description",
        groundingInstructions: (selectedScenarioData as any)?.groundingInstructions || "",
        originalText: JSON.stringify({
          description: selectedScenarioData?.description || "Scenario Description",
          groundingInstructions: (selectedScenarioData as any)?.groundingInstructions || "",
          userPrompt: userInputText
        })
      };
    }

    // Show only the user input in chat history
    const userMessage: ChatMessage = {
      role: "user",
      text: userInputText,
      hasAttachment,
      attachmentData
    };
    setMessages((prev) => [...prev, userMessage]);

    // Track user message send event
    const userId = sessionStorage.getItem("userId") || "unknown";
    logEvent("UserMessage_Sent", "AI Hub - Copilot Chat", "CopilotChat", {
      userId: userId
    });

    // Count current user messages and track engaging session when reaching 5
    const currentUserMessageCount = messages.filter((m) => m.role === "user").length + 1;
    if (currentUserMessageCount === 3) {
      logEvent("EngagingSession_Reached", "AI Hub - Copilot Chat", "CopilotChat", {
        userId: userId,
        sessionId: currentSessionId
      });
    }

    setInput("");
    setAttachedFile(null);
    setAttachedImages([]);

    if (textareaRef.current) textareaRef.current.style.height = "auto";
    if (fileInputRef.current) fileInputRef.current.value = "";

    setLoading(true);

    try {
      let messageContent = userInputText;

      // If a file is attached → replace messageContent with file text
      if (attachedFile) {
        messageContent = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result as string);
          reader.onerror = () => reject(new Error("Failed to read the attached file"));
          reader.readAsText(attachedFile);
        });
      }

      // If images are attached → extract text and append it to the prompt
      if (attachedImages.length > 0) {
        const worker = await createWorker("eng");
        try {
          const imageTextParts: string[] = [];
          for (let i = 0; i < attachedImages.length; i++) {
            const image = attachedImages[i];
            const {
              data: { text: extractedText }
            } = await worker.recognize(image);
            const normalizedExtractedText = (extractedText || "").trim() || "No text could be extracted from the image.";

            imageTextParts.push(`[Image ${i + 1}: ${image.name}]\n${normalizedExtractedText}`);
          }

          const imageTextSection = `Text found in the attached image\n\n${imageTextParts.join("\n\n")}`;
          const basePrompt = messageContent.trim();
          messageContent = basePrompt ? `${basePrompt}\n\n${imageTextSection}` : imageTextSection;
        } finally {
          await worker.terminate();
        }
      }

      // Streaming (SSE) path — starts rendering the response immediately.
      if (copilotStreamingEnabled) {
        await runStreamingSend(messageContent, userInputText, userId);
        return;
      }

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 300000);

      let response;

      try {
        // Check if this is the first message with a selected scenario (new chat)
        if (currentSessionId === "" && selectedScenario !== null) {
          // Find the selected scenario to get description and grounding instructions
          const selectedScenarioData = scenarios.find((s) => s.scenarioId === selectedScenario);

          // Create JSON structure for first message
          const firstMessageJson = {
            description: selectedScenarioData?.description || "Scenario Description",
            groundingInstructions: (selectedScenarioData as any)?.groundingInstructions || "",
            userPrompt: messageContent
          };

          // Call InitiateCopilotChatSession for first message with scenario
          response = await fetch(`${BASE_URL}/InitiateCopilotChatSession`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${sessionStorage.getItem("token")}`
            },
            body: JSON.stringify({
              SessionId: "",
              Message: JSON.stringify(firstMessageJson),
              ScenarioId: selectedScenario,
              ScenarioName: selectedScenarioData?.scenarioName || "Default"
            }),
            signal: controller.signal
          });
        } else {
          // Call GetChatSessionMessageResponse for subsequent messages
          response = await fetch(`${BASE_URL}/GetChatSessionMessageResponse`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${sessionStorage.getItem("token")}`
            },
            body: JSON.stringify({
              SessionId: currentSessionId,
              Message: messageContent,
              ScenarioId: currentSessionScenario?.scenarioId,
              ScenarioName: currentSessionScenario?.scenarioName
            }),
            signal: controller.signal
          });
        }
      } finally {
        clearTimeout(timeoutId);
      }

      // Check for HTTP error responses
      if (!response.ok) {
        throw new Error(`API returned status ${response.status}`);
      }

      const data = await response.json();

      if (!data.messages || data.messages.length === 0) {
        throw new Error("Empty response from API");
      }

      if (currentSessionId === "" && data.sessionId) {
        setCurrentSessionId(data.sessionId);

        // Save scenario information for this session
        if (selectedScenario !== null) {
          const scenario = scenarios.find((s) => s.scenarioId === selectedScenario);
          setCurrentSessionScenario({
            scenarioId: selectedScenario,
            scenarioName: scenario ? scenario.scenarioName : "Default",
            scenarioGrouping: scenario ? (scenario as any).scenarioGrouping : undefined,
            state: scenario ? scenario.state : ""
          });
        }
      }

      // Prepare new session title from user input (always use clean user input)
      // Use word-boundary truncation to stay under 40 characters
      let newTitle = userInputText;
      if (userInputText.length > 40) {
        const words = userInputText.split(" ");
        let truncated = "";

        for (const word of words) {
          // Check if adding this word (plus space if not first word) would exceed the limit
          const potentialLength = truncated ? truncated.length + 1 + word.length : word.length;
          if (potentialLength + 3 <= 40) { // +3 for "..."
            truncated += (truncated ? " " : "") + word;
          } else {
            break;
          }
        }

        newTitle = truncated + "...";
      }

      // Update session name on backend
      const sessionIdToUpdate = data.sessionId || currentSessionId;
      if (sessionIdToUpdate) {
        try {
          await fetch(`${BASE_URL}/RenameChatSession`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${sessionStorage.getItem("token")}`
            },
            body: JSON.stringify({
              sessionId: sessionIdToUpdate,
              sessionName: newTitle
            })
          });
        } catch (err) {
          console.error("Failed to update session name:", err);
        }
      }

      setSessions((prev) =>
        prev.map((s) =>
          s.id === "" || s.id === currentSessionId || s.id === data.sessionId
            ? {
              ...s,
              id: data.sessionId || s.id,
              // Always update title with the latest user message
              title: newTitle,
              scenarioId: selectedScenario !== null ? selectedScenario : s.scenarioId, // Store scenarioId
              lastUpdated: new Date().toISOString()
            }
            : s
        )
      );

      // Assuming data.messages is an array of message objects from your API
      const newMessages: ChatMessage[] = data.messages.map((m: any) => ({
        id: m.id, // Message ID from API for reaction tracking
        role: m.role === "user" ? "user" : "ai",
        text: m.text,
        images: m.images || [],
        content: m.content,
        functionCalls: m.functionCalls || [],
        action: m.action ?? null, // Reaction state: true=liked, false=disliked, null=no reaction
        hasAttachment: undefined, // Only user messages from UI state have attachments
        attachmentData: undefined
      }));

      // Check for agent errors in the response
      const hasAgentError = newMessages.some((msg) => {
        // Check if message text contains the exact error text
        return msg.text?.includes("AI Agent Response Error");
      });

      if (hasAgentError) {
        logEvent("CopilotChat_Failure", "AI Hub - Copilot Chat", "CopilotChat", {
          userId: userId,
          sessionId: data.sessionId || currentSessionId,
          scenarioId: currentSessionScenario?.scenarioId || selectedScenario,
          failureType: "AGENT_ERROR",
          errorMessage:
            newMessages.find((m) => m.role === "ai")?.text?.substring(0, 200) || "Agent returned error in response"
        });
        throw new Error("Agent returned an error response");
      }

      // Append all new messages to previous list
      setMessages((prev) => [...prev, ...newMessages]);

      // Clear file after send
      setAttachedFile(null);
      setAttachedImages([]);
    } catch (err: any) {
      // Log failure for any error in the catch block
      logEvent("CopilotChat_Failure", "AI Hub - Copilot Chat", "CopilotChat", {
        userId: userId,
        sessionId: currentSessionId,
        scenarioId: currentSessionScenario?.scenarioId || selectedScenario,
        failureType: "API_CALL_FAILURE",
        errorMessage: err.message || "Unknown error"
      });

      // Keep the user message visible and show a friendly AI error response in-thread
      // so the conversation is never left in a broken state
      setMessages((prev) => [...prev, makeErrorMessage(userInputText)]);
    } finally {
      setLoading(false);
      if (chatContainerRef.current) {
        chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
      }
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;

    // If file is attached and user deletes it completely, remove the file
    if ((attachedFile || attachedImages.length > 0) && value === "") {
      removeFile();
    }

    setInput(value);

    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = textareaRef.current.scrollHeight + "px";
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      loading ? handleStop() : handleSend();
    }

    // Remove attached file with backspace if no text
    if (e.key === "Backspace" && (attachedFile || attachedImages.length > 0) && input === "") {
      removeFile();
    }
  };

  const handleStop = () => {
    // Abort an in-flight stream (partial answer is kept + persisted by the agent).
    if (streamAbortRef.current) {
      streamAbortRef.current.abort();
      streamAbortRef.current = null;
      return;
    }
    setLoading(false);
    setMessages((prev) => [...prev, { role: "ai", text: "[Response stopped by user]" }]);
  };

  const openRenameDialog = (session: ChatSession) => {
    setRenameSessionId(session.id);
    setRenameInput(session.title);
    setRenameDialogOpen(true);
  };

  const handleRename = async () => {
    if (!renameSessionId || !renameInput.trim()) return;

    // Log rename chat button click
    logEvent("RenameChatButton_Clicked", "AI Hub - Copilot Chat", "CopilotChat", {
      sessionId: renameSessionId,
      newSessionName: renameInput
    });

    const res = await fetch(`${BASE_URL}/RenameChatSession`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${sessionStorage.getItem("token")}`
      },
      body: JSON.stringify({ sessionId: renameSessionId, sessionName: renameInput })
    });

    if (res.ok) {
      setSessions((prev) => prev.map((s) => (s.id === renameSessionId ? { ...s, title: renameInput } : s)));
      setRenameDialogOpen(false);
    }
  };

  const handleSubmitFeedback = async (rating: number | null, feedbackText: string) => {
    // Require either rating or feedback text
    if (!feedbackText.trim() && rating === null) return;
    if (!currentSessionId) return;

    setIsSubmittingFeedback(true);
    try {
      // Log feedback submission
      logEvent("FeedbackSubmitted", "AI Hub - Copilot Chat", "CopilotChat", {
        sessionId: currentSessionId,
        scenarioId: currentSessionScenario?.scenarioId,
        scenarioName: currentSessionScenario?.scenarioName,
        feedbackLength: feedbackText.length,
        rating: rating
      });

      // Call API to save session feedback
      await saveSessionFeedback({
        sessionId: currentSessionId,
        rating: rating,
        feedback: feedbackText.trim() || null
      }).unwrap();

      // Close the dialog
      setIsFeedbackDialogOpen(false);
      setSuccessToastMessage("Feedback submitted successfully!");
      setShowCopyToast(true);
      setTimeout(() => setShowCopyToast(false), 3000);
    } catch (error) {
      console.error("Failed to submit feedback:", error);
      setErrorToast("Failed to submit feedback. Please try again.");
      setTimeout(() => setErrorToast(""), 3000);
    } finally {
      setIsSubmittingFeedback(false);
    }
  };

  const handleDeleteSession = async (sessionId: string) => {
    if (!sessionId) return;

    // Log delete chat button click
    logEvent("DeleteChatButton_Clicked", "AI Hub - Copilot Chat", "CopilotChat", {
      sessionId: sessionId
    });

    const res = await fetch(`${BASE_URL}/DeleteChatSession`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${sessionStorage.getItem("token")}`
      },
      body: JSON.stringify(sessionId)
    });
    if (res.ok) {
      setSessions((prev) => {
        const updatedSessions = prev.filter((s) => s.id !== sessionId);
        if (currentSessionId === sessionId) {
          if (updatedSessions.length > 0) {
            const topSession = updatedSessions[0];
            setCurrentSessionId(topSession.id);
            loadSessionHistory(topSession.id);
          } else {
            // Set flag to create new chat after state updates
            setNeedsNewChatAfterDelete(true);
          }
        }
        return updatedSessions;
      });
    }
  };

  const handleShareSession = async (sessionId: string) => {
    // Log share button click
    logEvent("ShareChatButton_Clicked", "AI Hub - Copilot Chat", "CopilotChat", {
      sessionId: sessionId,
      scenarioId: currentSessionScenario?.scenarioId,
      scenarioName: currentSessionScenario?.scenarioName,
      calledFrom: "MoreOptionsMenu"
    });

    try {
      const shareUrl = `${window.location.origin}/aihub?sessionId=${sessionId}`;
      await navigator.clipboard.writeText(shareUrl);
      setSuccessToastMessage("Share link copied to clipboard!");
      setShowCopyToast(true);
      setTimeout(() => setShowCopyToast(false), 3000);
    } catch (error) {
      console.error("Failed to copy share link:", error);
    }
    setContextMenuProps(null);
  };

  // Handle message reaction (like/dislike) for agent responses
  // action: true = like, false = dislike, null = toggle off (remove reaction)
  const handleMessageReaction = async (
    messageId: string | undefined,
    action: boolean | null,
    feedbackData?: { whatWentWell: string; whatDidNotGoWell: string; goodToHave: string }
  ) => {
    if (!currentSessionId || !messageId) return;

    // Log the reaction event
    const reactionType = action === true ? "like" : action === false ? "dislike" : "removed";
    logEvent("MessageReaction_Submitted", "AI Hub - Copilot Chat", "CopilotChat", {
      sessionId: currentSessionId,
      messageId: messageId,
      reaction: reactionType,
      scenarioId: currentSessionScenario?.scenarioId,
      scenarioName: currentSessionScenario?.scenarioName,
      hasFeedbackText: feedbackData ? (feedbackData.whatWentWell || feedbackData.whatDidNotGoWell || feedbackData.goodToHave ? true : false) : false
    });

    try {
      await saveMessageReaction({
        messageId: messageId,
        sessionId: currentSessionId,
        action: action,
        whatWentWell: feedbackData?.whatWentWell || null,
        whatDidNotGoWell: feedbackData?.whatDidNotGoWell || null,
        goodToHave: feedbackData?.goodToHave || null,
        // eslint-disable-next-line no-undef
        environment: process.env.REACT_APP_ENVIRONMENT || null
      }).unwrap();

      // Show success toast for reaction
      if (action === true) {
        setSuccessToastMessage("Feedback submitted - Response liked!");
      } else if (action === false) {
        setSuccessToastMessage("Feedback submitted - Thank you for your input!");
      } else {
        setSuccessToastMessage("Reaction removed!");
      }
      setShowCopyToast(true);
      setTimeout(() => setShowCopyToast(false), 2000);
    } catch (error) {
      setErrorToast("Failed to save feedback. Please try again.");
      setTimeout(() => setErrorToast(""), 3000);
    }
  };

  const openContextMenu = (session: ChatSession, e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (session.id === "") return;
    setContextMenuProps({
      items: [
        {
          key: "share",
          text: "Share",
          iconProps: { iconName: "Share" },
          onClick: () => handleShareSession(session.id)
        },
        {
          key: "rename",
          text: "Rename",
          iconProps: { iconName: "Edit" },
          onClick: () => openRenameDialog(session)
        },
        {
          key: "delete",
          text: "Delete",
          iconProps: { iconName: "Delete" },
          onClick: () => handleDeleteSession(session.id)
        }
      ],
      target: e.currentTarget as HTMLElement,
      onDismiss: () => setContextMenuProps(null)
    });
  };

  useEffect(() => {
    if (chatContainerRef.current && messages.length > 0) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [messages, loading]);

  const handleSessionSelect = (sessionId: string) => {
    // Don't allow selection of empty/uninitialized sessions
    if (sessionId === "") {
      return;
    }

    // Log browsing past chat
    const selectedSession = sessions.find((s) => s.id === sessionId);
    logEvent("PastChatBrowsed", "AI Hub - Copilot Chat", "CopilotChat", {
      sessionId: sessionId,
      sessionTitle: selectedSession?.title,
      scenarioId: selectedSession?.scenarioId
    });

    // Remove any empty "New Chat" sessions
    const cleanedSessions = sessions.filter(s => !(s.id === "" && s.messages.length === 0));
    setSessions(cleanedSessions);
    setCurrentSessionId(sessionId);

    // Update expanded scenarios to show only the group containing the selected session
    if (selectedSession) {
      const selectedScenarioGrouping = getSessionScenarioGrouping(selectedSession.scenarioId ?? 0);

      // Close all groups and open only the one containing the selected session
      setExpandedScenarios({ [selectedScenarioGrouping]: true });
    }

    // Ensure scenario selection is hidden when switching sessions
    setShowScenarioSelection(false);
    // Keep chat area visible while loading to prevent brief scenario/AMA flashes
    setShowChat(true);

    // Clear readonly mode and session when switching to user's session
    if (isReadonlyMode || readonlySession) {
      setIsReadonlyMode(false);
      setReadonlySession(null);
      setReadonlySessionOwner("");
      // Update URL to remove sessionId parameter
      window.history.pushState({}, "", "/aihub");
    }

    loadSessionHistory(sessionId);
  };

  const handleToggleExpand = (key: string) => {
    setExpandedStates((prev: any) => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  // New handlers for ScenarioSelection component
  const handleScenarioNext = () => {
    // This will now show the sample prompts section within ScenarioSelection
    if (selectedScenario !== null) {
      const scenario = scenarios.find((s) => s.scenarioId === selectedScenario);
      if (scenario) {
        // Set the scenario name immediately when proceeding to next step
        setCurrentSessionScenario({
          scenarioId: scenario.scenarioId,
          scenarioName: scenario.scenarioName,
          scenarioGrouping: (scenario as any).scenarioGrouping,
          state: scenario.state
        });
      }
    }
    // Clear the input text area
    setInput("");
  };

  // Handler to set scenario info from ScenarioSelection component
  const handleSetScenarioInfo = (scenarioId: number, scenarioName: string, scenarioGrouping?: string) => {
    const scenario = scenarios.find((s) => s.scenarioId === scenarioId);
    setSelectedScenario(scenarioId);
    setCurrentSessionScenario({
      scenarioId: scenarioId,
      scenarioName: scenarioName,
      scenarioGrouping: scenarioGrouping || (scenario ? (scenario as any).scenarioGrouping : undefined),
      state: scenario ? scenario.state : ""
    });
  };

  const handleContinueChat = async () => {
    if (!currentSessionId) return;

    setIsCopyingChat(true);
    try {
      // Get the current session details for sessionName and scenarioId
      const currentSession = readonlySession || sessions.find((s) => s.id === currentSessionId);
      const sessionName = currentSession?.title || "Continued Chat";
      const scenarioId = currentSessionScenario?.scenarioId || 0;

      // Call the API to replicate the session and get a new sessionId
      const response = await fetch(`${BASE_URL}/GetCopilotChatReplicatedSessionId`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${sessionStorage.getItem("token")}`
        },
        body: JSON.stringify({
          sessionId: currentSessionId,
          sessionName: sessionName,
          scenarioId: scenarioId
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to replicate session: ${response.statusText}`);
      }

      const newSessionId = await response.text();

      if (newSessionId) {
        // Exit readonly mode
        setIsReadonlyMode(false);
        setReadonlySession(null);
        setReadonlySessionOwner("");

        // Update URL to remove sessionId parameter
        window.history.pushState({}, "", "/aihub");

        // Set the new session as current
        setCurrentSessionId(newSessionId);

        // The messages are already loaded, just keep them displayed
        // User can now continue chatting with the new session

        // Refresh the sessions list to include the new session
        const res = await fetch(`${BASE_URL}/GetUserChatSessions`, {
          headers: { Authorization: `Bearer ${sessionStorage.getItem("token")}` }
        });
        const sessionsData = await res.json();
        const sorted = sessionsData
          .sort((a: any, b: any) => new Date(b.lastUpdatedTime).getTime() - new Date(a.lastUpdatedTime).getTime())
          .map((s: any) => ({
            id: s.sessionId,
            title: s.sessionName,
            messages: [],
            lastUpdated: s.lastUpdatedTime,
            scenarioId: s.scenarioId
          }));
        setSessions(sorted);
      }
    } catch (error) {
      console.error("Failed to continue chat:", error);
    } finally {
      setIsCopyingChat(false);
    }
  };

  return (
    <div
      style={{
        display: "flex",
        height: "calc(77vh + 20px)", // Increased height
        fontFamily:
          "\"Segoe UI\", \"Segoe UI Web (West European)\", \"Segoe UI\", -apple-system, BlinkMacSystemFont, Roboto, \"Helvetica Neue\", sans-serif",
        background: "#f8f9fa",
        marginTop: "15px", // Space from navigation
        marginBottom: "15px" // Bottom spacing
      }}
    >
      {/* Animation styles */}
      <style>
        {`
          /* Modern Copilot-inspired Typography */
          * {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif;
            -webkit-font-smoothing: antialiased;
            -moz-osx-font-smoothing: grayscale;
          }
          
          @keyframes blink {
            0%,40%, 80%, 100% { opacity: 0; }
            40% { opacity: 1; }
          }
          @keyframes fadeInUp {
            0% { opacity: 0; transform: translateY(20px); }
            100% { opacity: 1; transform: translateY(0); }
          }
          @keyframes slideIn {
            0% { opacity: 0; transform: translateX(-10px); }
            100% { opacity: 1; transform: translateX(0); }
          }
          
          /* Clean, Modern Typography - Copilot Style */
          .copilot-text {
            font-size: 14px;
            font-weight: 400;
            line-height: 1.6;
            letter-spacing: -0.01em;
          }
          
          .copilot-title {
            font-size: 24px;
            font-weight: 600;
            line-height: 1.3;
            color: #1a1a1a;
            letter-spacing: -0.02em;
          }
          
          .copilot-subtitle {
            font-size: 16px;
            font-weight: 500;
            line-height: 1.4;
            color: #323232;
            letter-spacing: -0.01em;
          }
          
          .copilot-label {
            font-size: 12px;
            font-weight: 500;
            line-height: 1.5;
            color: #616161;
            letter-spacing: 0;
          }
          
          .copilot-card-title {
            font-size: 13px;
            font-weight: 600;
            line-height: 1.4;
            color: #242424;
            letter-spacing: -0.01em;
          }
          
          .copilot-card-description {
            font-size: 12px;
            font-weight: 400;
            line-height: 1.6;
            color: #616161;
            letter-spacing: 0;
          }
          
          textarea, input {
            font-size: 14px;
            font-weight: 400;
            line-height: 1.5;
            color: #242424;
          }
          
          textarea::placeholder, input::placeholder {
            color: #999;
            font-weight: 400;
          }
          
          /* Responsive adjustments */
          @media (max-width: 1200px) {
            .copilot-text { font-size: 13px; }
            .copilot-title { font-size: 20px; }
            .copilot-subtitle { font-size: 14px; }
            textarea, input { font-size: 13px; }
          }
          
          @media (min-width: 1601px) {
            .copilot-text { font-size: 15px; }
            .copilot-title { font-size: 28px; }
            .copilot-subtitle { font-size: 18px; }
            textarea, input { font-size: 15px; }
          }
        `}
      </style>

      {/* Sidebar */}
      <div
        style={{
          width: sidebarCollapsed ? "60px" : "240px",
          background: "linear-gradient(180deg, #fafbfc 0%, #f6f8fa 100%)",
          borderRight: "1px solid #e1e4e8",
          padding: sidebarCollapsed ? "15px 10px" : "16px",
          display: "flex",
          flexDirection: "column",
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          boxShadow: "2px 0 12px rgba(0, 0, 0, 0.06)",
          height: "100%", // Full height of parent
          flexShrink: 0 // Prevent shrinking
        }}
      >
        {/* Toggle Button */}
        <button
          onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          style={{
            marginBottom: "12px",
            padding: "10px",
            background: "#ffffff",
            border: "1px solid #e1e4e8",
            borderRadius: "10px",
            cursor: "pointer",
            fontSize: "16px",
            color: "#586069",
            transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 1px 3px rgba(0, 0, 0, 0.08)"
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "#0078D7";
            e.currentTarget.style.borderColor = "#0078D7";
            e.currentTarget.style.color = "#ffffff";
            e.currentTarget.style.transform = "scale(1.05)";
            e.currentTarget.style.boxShadow = "0 4px 12px rgba(0, 120, 215, 0.3)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "#ffffff";
            e.currentTarget.style.borderColor = "#e1e4e8";
            e.currentTarget.style.color = "#586069";
            e.currentTarget.style.transform = "scale(1)";
            e.currentTarget.style.boxShadow = "0 1px 3px rgba(0, 0, 0, 0.08)";
          }}
        >
          <Icon iconName={sidebarCollapsed ? "DoubleChevronRight" : "DoubleChevronLeft"} />
        </button>

        {!sidebarCollapsed && (
          <>
            {/* New Chat Button */}
            <button
              onClick={() => handleNewChat(false)}
              disabled={loading}
              style={{
                marginBottom: "12px",
                padding: "10px 12px",
                background: loading
                  ? "linear-gradient(135deg, #e0e0e0 0%, #d0d0d0 100%)"
                  : "linear-gradient(135deg, #0078D7 0%, #106ebe 100%)",
                color: loading ? "#999" : "#fff",
                border: "none",
                borderRadius: "10px",
                cursor: loading ? "not-allowed" : "pointer",
                fontSize: "13px",
                fontWeight: 600,
                display: "flex",
                alignItems: "center",
                gap: "8px",
                justifyContent: "center",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                boxShadow: loading ? "0 2px 4px rgba(0, 0, 0, 0.1)" : "0 4px 14px rgba(0, 120, 215, 0.35)",
                position: "relative",
                overflow: "hidden"
              }}
              onMouseEnter={(e) => {
                if (!loading) {
                  e.currentTarget.style.transform = "translateY(-2px) scale(1.02)";
                  e.currentTarget.style.boxShadow = "0 8px 20px rgba(0, 120, 215, 0.45)";
                  e.currentTarget.style.background = "linear-gradient(135deg, #1084e8 0%, #0066b8 100%)";
                }
              }}
              onMouseLeave={(e) => {
                if (!loading && !isReadonlyMode) {
                  e.currentTarget.style.transform = "translateY(0) scale(1)";
                  e.currentTarget.style.boxShadow = "0 4px 14px rgba(0, 120, 215, 0.35)";
                  e.currentTarget.style.background = "linear-gradient(135deg, #0078D7 0%, #106ebe 100%)";
                }
              }}
            >
              <Icon iconName={loading ? "Sync" : "Add"} style={{ fontSize: "16px" }} />
              <span style={{ letterSpacing: "0.3px" }}>{loading ? "Processing..." : "New Chat"}</span>
            </button>

            {/* Chats Header - Show different header based on mode */}
            {readonlySession ? (
              /* Readonly Session Section Header */
              <div
                style={{
                  marginBottom: "14px",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  paddingLeft: "2px",
                  paddingBottom: "10px",
                  borderBottom: "2px solid #ffc107"
                }}
              >
                <div
                  style={{
                    width: "32px",
                    height: "32px",
                    borderRadius: "8px",
                    background: "linear-gradient(135deg, #ffc107 0%, #ff9800 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0 2px 8px rgba(255, 193, 7, 0.35)"
                  }}
                >
                  <Icon iconName="Lock" style={{ fontSize: "14px", color: "#ffffff" }} />
                </div>
                <h3
                  style={{
                    margin: 0,
                    fontSize: "12px",
                    fontWeight: 700,
                    color: "#856404",
                    textTransform: "uppercase",
                    letterSpacing: "0.8px"
                  }}
                >
                  Shared Session
                </h3>
              </div>
            ) : (
              /* Normal Chat History Header */
              <div
                style={{
                  marginBottom: "14px",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  paddingLeft: "2px",
                  paddingBottom: "10px",
                  borderBottom: "2px solid #e1e4e8"
                }}
              >
                <div
                  style={{
                    width: "32px",
                    height: "32px",
                    borderRadius: "8px",
                    background: "linear-gradient(135deg, #0078D7 0%, #106ebe 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0 2px 8px rgba(0, 120, 215, 0.35)"
                  }}
                >
                  <Icon iconName="Comment" style={{ fontSize: "14px", color: "#ffffff" }} />
                </div>
                <h3
                  style={{
                    margin: 0,
                    fontSize: "12px",
                    fontWeight: 700,
                    color: "#24292e",
                    textTransform: "uppercase",
                    letterSpacing: "0.8px"
                  }}
                >
                  Chat History
                </h3>
              </div>
            )}

            {/* Readonly Session List */}
            {readonlySession && (
              <>
                <ul
                  style={{
                    listStyle: "none",
                    padding: 0,
                    margin: "0 0 14px 0",
                    paddingRight: "4px"
                  }}
                >
                  <li
                    style={{
                      padding: "10px 12px",
                      marginBottom: "8px",
                      borderRadius: "12px",
                      cursor: "default",
                      background: "linear-gradient(135deg, #fff3cd 0%, #ffe8a1 100%)",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      border: "1.5px solid #ffc107",
                      boxShadow: "0 3px 10px rgba(255, 193, 7, 0.25)"
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                        flex: 1,
                        overflow: "hidden"
                      }}
                    >
                      <div
                        style={{
                          width: "28px",
                          height: "28px",
                          borderRadius: "8px",
                          background: "linear-gradient(135deg, #ffc107 0%, #ff9800 100%)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                          boxShadow: "0 2px 6px rgba(255, 193, 7, 0.3)"
                        }}
                      >
                        <Icon
                          iconName="View"
                          style={{
                            fontSize: "14px",
                            color: "#ffffff"
                          }}
                        />
                      </div>
                      <span
                        title={readonlySession.title}
                        className="copilot-label"
                        style={{
                          fontWeight: 600,
                          color: "#856404",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap"
                        }}
                      >
                        {readonlySession.title.length > 18
                          ? readonlySession.title.substring(0, 18) + "..."
                          : readonlySession.title}
                      </span>
                    </div>
                  </li>
                </ul>
                {/* Separator between shared and user sessions */}
                <div
                  style={{
                    borderBottom: "2px solid #e1e4e8",
                    marginBottom: "14px",
                    paddingBottom: "10px"
                  }}
                >
                  <h3
                    style={{
                      margin: 0,
                      fontSize: "12px",
                      fontWeight: 700,
                      color: "#24292e",
                      textTransform: "uppercase",
                      letterSpacing: "0.8px",
                      paddingLeft: "2px"
                    }}
                  >
                    Your Chats
                  </h3>
                </div>
              </>
            )}

            {/* Categorized Sessions by Scenario */}
            <div
              className="copilot-sessions-list"
              style={{
                flex: 1,
                overflowY: "auto",
                paddingRight: "4px",
              }}>
              {Object.entries(sessionsByScenario)
                .sort(([a], [b]) => {
                  // Starred always comes first
                  if (a === "Starred") return -1;
                  if (b === "Starred") return 1;
                  // Recents comes second
                  if (a === "Recents") return -1;
                  if (b === "Recents") return 1;
                  // General comes third
                  if (a === "General") return -1;
                  if (b === "General") return 1;
                  // Rest are sorted alphabetically
                  return a.localeCompare(b);
                })
                .map(([scenarioGrouping, { sessions: scenarioSessions, scenarioName, scenarioColor }]) => {
                  const isExpanded = expandedScenarios[scenarioGrouping] ?? false;

                  return (
                    <React.Fragment key={scenarioGrouping}>
                      <div style={{ marginBottom: "12px" }}>
                        {/* Scenario Header */}
                        <div
                          onClick={() => toggleScenarioExpansion(scenarioGrouping)}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            padding: "12px 14px",
                            background: isExpanded
                              ? `linear-gradient(135deg, #ffffff 0%, ${scenarioColor}06 100%)`
                              : "#ffffff",
                            borderRadius: isExpanded ? "10px 10px 0 0" : "10px",
                            cursor: "pointer",
                            border: `1px solid ${isExpanded ? `${scenarioColor}44` : "#e8ebef"}`,
                            borderLeft: `3px solid ${isExpanded ? scenarioColor : `${scenarioColor}55`}`,
                            borderBottom: isExpanded ? "none" : "1px solid #e8ebef",
                            transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
                            boxShadow: isExpanded
                              ? `0 2px 8px ${scenarioColor}15`
                              : "0 1px 3px rgba(0,0,0,0.04)",
                          } as React.CSSProperties}
                          onMouseEnter={(e) => {
                            if (!isExpanded) {
                              e.currentTarget.style.background = `linear-gradient(135deg, #ffffff 0%, ${scenarioColor}08 100%)`;
                              e.currentTarget.style.borderColor = `${scenarioColor}55`;
                              e.currentTarget.style.borderLeftColor = scenarioColor;
                              e.currentTarget.style.boxShadow = `0 3px 10px ${scenarioColor}12`;
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!isExpanded) {
                              e.currentTarget.style.background = "#ffffff";
                              e.currentTarget.style.borderColor = "#e8ebef";
                              e.currentTarget.style.borderLeftColor = `${scenarioColor}55`;
                              e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.04)";
                            }
                          }}
                        >
                          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                            {/* Category Icon */}
                            <div style={{
                              width: "32px",
                              height: "32px",
                              borderRadius: "8px",
                              background: isExpanded
                                ? `linear-gradient(135deg, ${scenarioColor} 0%, ${scenarioColor}cc 100%)`
                                : `linear-gradient(135deg, ${scenarioColor}20 0%, ${scenarioColor}10 100%)`,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexShrink: 0,
                              transition: "all 0.25s ease",
                              boxShadow: isExpanded ? `0 2px 6px ${scenarioColor}40` : "none",
                            }}>
                              <Icon
                                iconName={
                                  scenarioGrouping === "Starred" ? "FavoriteStarFill" :
                                    scenarioGrouping === "Recents" ? "Recent" : "BulletedList"
                                }
                                style={{
                                  fontSize: "15px",
                                  color: isExpanded ? "#ffffff" : scenarioColor,
                                  transition: "color 0.2s ease",
                                }}
                              />
                            </div>
                            <div style={{ minWidth: 0 }}>
                              <span style={{
                                fontSize: "14px",
                                fontWeight: 600,
                                color: isExpanded ? "#1a1a1a" : "#24292e",
                                display: "block",
                                letterSpacing: "0.2px",
                              }}>
                                {scenarioName}
                              </span>
                              <span style={{
                                fontSize: "11px",
                                color: isExpanded ? scenarioColor : "#8b949e",
                                fontWeight: 500,
                                transition: "color 0.2s ease",
                              }}>
                                {scenarioSessions.length} chat{scenarioSessions.length !== 1 ? "s" : ""}
                              </span>
                            </div>
                          </div>
                          <div style={{
                            width: "24px",
                            height: "24px",
                            borderRadius: "6px",
                            background: isExpanded ? `${scenarioColor}15` : "#f6f8fa",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            transition: "all 0.25s ease",
                          }}>
                            <Icon
                              iconName={isExpanded ? "ChevronDown" : "ChevronRight"}
                              style={{
                                fontSize: "11px",
                                color: isExpanded ? scenarioColor : "#8b949e",
                                transition: "all 0.2s ease",
                              }}
                            />
                          </div>
                        </div>

                        {/* Sessions List */}
                        {isExpanded && (
                          <div
                            style={{
                              paddingLeft: "8px",
                              paddingRight: "6px",
                              marginLeft: "0px",
                              paddingBottom: "12px",
                              borderLeft: `3px solid ${scenarioColor}`,
                              borderRight: `1px solid ${scenarioColor}44`,
                              borderBottom: `1px solid ${scenarioColor}44`,
                              paddingTop: "10px",
                              backgroundColor: "#f8fafc",
                              borderRadius: "0 0 10px 10px",
                              position: "relative",
                              marginBottom: "12px",
                              boxShadow: `0 4px 12px ${scenarioColor}10`,
                            }}>
                            {scenarioSessions.map((s, index) => (
                              <div
                                key={s.id || `session-${scenarioGrouping}-${index}`}
                                style={{
                                  padding: "8px 10px",
                                  marginBottom: "5px",
                                  marginRight: "4px",
                                  borderRadius: "8px",
                                  cursor: "pointer",
                                  background: currentSessionId === s.id
                                    ? `linear-gradient(135deg, #ffffff 0%, ${scenarioColor}08 100%)`
                                    : "#ffffff",
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  border: currentSessionId === s.id
                                    ? `1px solid ${scenarioColor}66`
                                    : "1px solid #e8ebef",
                                  borderRight: currentSessionId === s.id
                                    ? `3px solid ${scenarioColor}`
                                    : "3px solid transparent",
                                  transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
                                  boxShadow: currentSessionId === s.id
                                    ? `0 2px 8px ${scenarioColor}25`
                                    : "0 1px 2px rgba(0,0,0,0.04)",
                                  position: "relative",
                                  transform: "translateX(0)",
                                }}
                                onMouseEnter={(e) => {
                                  if (currentSessionId !== s.id) {
                                    e.currentTarget.style.background = "linear-gradient(135deg, #ffffff 0%, #f0f4ff 100%)";
                                    e.currentTarget.style.border = `1px solid ${scenarioColor}44`;
                                    e.currentTarget.style.borderRight = `3px solid ${scenarioColor}88`;
                                    e.currentTarget.style.boxShadow = `0 3px 10px ${scenarioColor}18`;
                                    e.currentTarget.style.transform = "translateX(4px)";
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  if (currentSessionId !== s.id) {
                                    e.currentTarget.style.background = "#ffffff";
                                    e.currentTarget.style.border = "1px solid #e8ebef";
                                    e.currentTarget.style.borderRight = "3px solid transparent";
                                    e.currentTarget.style.boxShadow = "0 1px 2px rgba(0,0,0,0.04)";
                                    e.currentTarget.style.transform = "translateX(0)";
                                  }
                                }}
                                onClick={() => handleSessionSelect(s.id)}
                              >
                                <div style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "8px",
                                  flex: 1,
                                  overflow: "hidden",
                                  minWidth: 0,
                                }}>
                                  <div style={{
                                    width: "24px",
                                    height: "24px",
                                    borderRadius: "6px",
                                    background: currentSessionId === s.id
                                      ? `linear-gradient(135deg, ${scenarioColor} 0%, ${scenarioColor}cc 100%)`
                                      : `linear-gradient(135deg, ${scenarioColor}20 0%, ${scenarioColor}10 100%)`,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    flexShrink: 0,
                                    transition: "all 0.25s ease",
                                    boxShadow: currentSessionId === s.id
                                      ? `0 2px 6px ${scenarioColor}40`
                                      : "none",
                                  }}>
                                    <Icon
                                      iconName="Chat"
                                      style={{
                                        fontSize: "11px",
                                        color: currentSessionId === s.id ? "#ffffff" : scenarioColor,
                                        transition: "color 0.2s ease",
                                      }}
                                    />
                                  </div>
                                  <div style={{
                                    flex: 1,
                                    minWidth: 0,
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: "2px",
                                  }}>
                                    <span
                                      title={s.title}
                                      className="copilot-label"
                                      style={{
                                        fontWeight: currentSessionId === s.id ? 600 : 500,
                                        color: currentSessionId === s.id ? "#1a1a1a" : "#3d4852",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                        fontSize: "12px",
                                        lineHeight: "1.3",
                                        transition: "color 0.2s ease",
                                      }}
                                    >
                                      {s.title}
                                    </span>
                                    {s.lastUpdated && (
                                      <span style={{
                                        fontSize: "10px",
                                        color: "#8b949e",
                                        fontWeight: 400,
                                      }}>
                                        {new Date(s.lastUpdated).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                                      </span>
                                    )}
                                  </div>
                                </div>
                                {s.id !== "" && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openContextMenu(s, e);
                                    }}
                                    style={{
                                      border: "none",
                                      background: "transparent",
                                      cursor: "pointer",
                                      padding: "6px",
                                      borderRadius: "6px",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                                      opacity: 0.5,
                                      transform: "scale(1)",
                                    }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.background = `${scenarioColor}18`;
                                      e.currentTarget.style.opacity = "1";
                                      e.currentTarget.style.transform = "scale(1.15) rotate(90deg)";
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.background = "transparent";
                                      e.currentTarget.style.opacity = "0.5";
                                      e.currentTarget.style.transform = "scale(1) rotate(0deg)";
                                    }}
                                  >
                                    <Icon iconName="MoreVertical" style={{ fontSize: "14px", color: scenarioColor }} />
                                  </button>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Separator after Recents category */}
                      {scenarioGrouping === "Recents" && (
                        <div
                          style={{
                            borderBottom: "2px solid #e1e4e8",
                            marginTop: "10px",
                            marginBottom: "20px",
                            paddingBottom: "0px"
                          }}
                        />
                      )}
                    </React.Fragment>
                  );
                })}
            </div>
          </>
        )}
      </div>

      {/* Fixed Gap Spacer */}
      <div style={{ width: "1.5%", minWidth: "15px", maxWidth: "25px", flexShrink: 0 }}></div>

      {/* Main Chat Area */}
      <div
        style={{
          flex: 1,
          padding: "15px 1.5% 10px 0", // Responsive padding
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-start",
          alignItems: "center",
          height: "100%", // Full height
          overflow: "hidden", // No scroll at this level
          minWidth: 0 // Allow flexbox to shrink properly
        }}
      >
        {/* Readonly Mode Banner */}
        {isReadonlyMode && (
          <div
            style={{
              width: "95%",
              padding: "20px 24px",
              background: "linear-gradient(135deg, #fff3cd 0%, #ffe8a1 100%)",
              border: "2px solid #ffc107",
              borderRadius: "16px",
              marginBottom: "15px",
              display: "flex",
              alignItems: "center",
              gap: "12px",
              boxShadow: "0 4px 16px rgba(255, 193, 7, 0.25)",
              flexShrink: 0 // Prevent banner from shrinking
            }}
          >
            <Icon iconName="Lock" style={{ fontSize: "24px", color: "#856404" }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: "16px", fontWeight: 700, color: "#856404", marginBottom: "4px" }}>
                Viewing Shared Chat (Read-Only)
              </div>
              <div style={{ fontSize: "13px", color: "#856404" }}>
                This is a shared chat session. Click &quot;Continue Chat&quot; below to create your own copy.
              </div>
            </div>

            {readonlySessionOwner && (
              <div
                style={{
                  fontSize: "12px",
                  color: "#856404",
                  fontStyle: "italic",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px"
                }}
              >
                <Icon iconName="Contact" style={{ fontSize: "14px" }} />
                <span>
                  Shared by: <strong>{readonlySessionOwner}</strong>
                </span>
              </div>
            )}
          </div>
        )}

        {/* Copilot Panel Header */}
        <div
          style={{
            display: "none",
            alignItems: "center",
            marginBottom: "20px",
            width: "80%",
            justifyContent: "space-between",
          }}
        >
          <h2 style={{ marginRight: "10px" }}>Security360 Copilot</h2>
          <IconButton
            iconProps={{ iconName: "ChatBot" }}
            title="Open Copilot"
            ariaLabel="Open Copilot"
            onClick={() => setIsOpen(true)}
          />
          <Panel
            isOpen={isOpen}
            onDismiss={() => setIsOpen(false)}
            type={PanelType.custom}
            headerText=""
            isLightDismiss
            styles={{
              main: { background: "#fafafa", width: 500 },
              content: { display: "flex", flexDirection: "column" },
            }}
          >
            <CopilotPanel />
          </Panel>
        </div>

        {/* Shared Session Loading - Show "Opening chat" spinner */}
        {isLoadingSharedSession && (
          <div
            style={{
              width: "100%",
              height: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: "20px"
            }}
          >
            <div style={{
              width: "40px",
              height: "40px",
              border: "3px solid #f3f3f3",
              borderTop: "3px solid #0078d4",
              borderRadius: "50%",
              animation: "spin 1s linear infinite"
            }}></div>
            <div style={{
              fontSize: "16px",
              color: "#666",
              fontWeight: 500
            }}>
              Opening chat...
            </div>
            <style>
              {`
                            @keyframes spin {
                                0% { transform: rotate(0deg); }
                                100% { transform: rotate(360deg); }
                            }
                        `}
            </style>
          </div>
        )}

        {/* New Chat - Show ScenarioSelection or Ask Me Anything */}
        {!isLoadingSharedSession && showScenarioSelection && messages.length === 0 && (
          <div
            style={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "flex-start",
              height: "96%",
              overflowY: "auto", // Allow scroll only in this section
            }}
          >
            {showScenarioSelection && (
              <ScenarioSelection
                scenarios={scenarios}
                scenariosLoading={scenariosLoading}
                selectedScenario={selectedScenario}
                onScenarioSelect={setSelectedScenario}
                onNext={handleScenarioNext}
                onSetScenarioInfo={handleSetScenarioInfo}
                selectedScenarioName={currentSessionScenario?.scenarioName}
                onPromptSelect={(prompt) => {
                  setInput(prompt);
                  // Auto-adjust textarea height after setting prompt
                  setTimeout(() => {
                    if (textareaRef.current) {
                      textareaRef.current.style.height = "auto";
                      textareaRef.current.style.height = textareaRef.current.scrollHeight + "px";
                    }
                  }, 100);
                }}
                ChatInput={ChatInput}
                inputProps={{
                  input,
                  loading,
                  attachedFile,
                  attachedImages,
                  isRecording,
                  isSpeechSupported,
                  onInputChange: handleInputChange,
                  onKeyDown: handleKeyDown,
                  onFileChange: handleFileChange,
                  onImageChange: handleImageChange,
                  onRemoveFile: removeFile,
                  onRemoveImage: removeImageAt,
                  onSend: handleSend,
                  onStop: handleStop,
                  onStartRecording: startRecording,
                  onStopRecording: stopRecording
                }}
              />
            )}
          </div>
        )}

        {/* Chat Container + Bottom Input */}
        {(messages.length > 0 || currentSessionId !== "") && (
          <div
            style={{
              width: "95%", // Responsive width
              flex: 1, // Take remaining available space
              display: "flex",
              flexDirection: "column",
              gap: "15px", // Reduced gap between chat and input
              minHeight: 0, // Allow flex shrinking
              paddingBottom: "25px" // Space between input and container bottom
            }}
          >
            <div
              ref={chatContainerRef}
              style={{
                width: "100%",
                flex: 1, // Take available space
                overflow: "auto",
                background: "linear-gradient(to bottom, #ffffff 0%, #fafbfc 100%)",
                borderRadius: "16px",
                padding: currentSessionScenario
                  ? "0 clamp(15px, 1.5%, 25px) clamp(15px, 1.5%, 25px) clamp(15px, 1.5%, 25px)"
                  : "clamp(15px, 1.5%, 25px)", // No top padding if scenario banner exists
                display: "flex",
                flexDirection: "column",
                position: "relative",
                boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08), 0 2px 8px rgba(0, 0, 0, 0.04)",
                border: "1px solid rgba(0, 0, 0, 0.06)"
              }}
            >
              {/* Scenario Badge - Fixed at top center */}
              {currentSessionScenario && currentSessionScenario.scenarioName !== "Loading..." && (
                <div
                  style={{
                    position: "sticky",
                    top: 0,
                    zIndex: 10,
                    marginBottom: "30px",
                    marginLeft: "calc(-1 * clamp(15px, 1.5%, 25px))", // Match container padding
                    marginRight: "calc(-1 * clamp(15px, 1.5%, 25px))", // Match container padding
                    marginTop: "calc(-1 * clamp(15px, 1.5%, 25px))", // Match container padding
                    background: "linear-gradient(to bottom, #ffffff 0%, rgba(255, 255, 255, 0.98) 100%)",
                    borderTopLeftRadius: "16px",
                    borderTopRightRadius: "16px",
                    backdropFilter: "blur(10px)",
                    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)"
                  }}
                >
                  <div
                    style={{
                      position: "relative",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      paddingTop: "12px",
                      paddingBottom: "12px",
                      paddingLeft: "20px",
                      paddingRight: "20px"
                    }}
                  >
                    {/* Tooltip over scenario name showing its description */}
                    <TooltipHost
                      content={(
                        scenarios.find((s) => s.scenarioId === currentSessionScenario.scenarioId)?.description || ""
                      ).replace(/\\n/g, "\n")}
                      styles={{ root: { display: "inline-flex" } }}
                      calloutProps={{
                        directionalHint: DirectionalHint.topCenter,
                        directionalHintFixed: true,
                        isBeakVisible: true,
                        beakWidth: 12,
                        gapSpace: 6,
                        styles: {
                          calloutMain: {
                            color: "white",
                            borderRadius: "8px",
                            padding: "12px 16px",
                            fontSize: "13px",
                            // Expand width for large descriptions to avoid vertical scroll
                            maxWidth:
                              (
                                scenarios.find((s) => s.scenarioId === currentSessionScenario.scenarioId)
                                  ?.description || ""
                              ).replace(/\\n/g, " ").length > 300
                                ? "600px"
                                : "380px",
                            whiteSpace: "pre-wrap",
                            wordWrap: "break-word",
                            lineHeight: "1.4",
                            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.3)"
                          }
                        }
                      }}
                    >
                      <div
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "10px",
                          padding: "12px 20px",
                          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                          borderRadius: "14px",
                          boxShadow: "0 6px 16px rgba(102, 126, 234, 0.35), 0 2px 4px rgba(102, 126, 234, 0.2)",
                          animation: "fadeInUp 0.5s ease-out"
                        }}
                      >
                        <Icon
                          iconName="Lightbulb"
                          style={{
                            fontSize: "16px",
                            color: "#fff"
                          }}
                        />
                        <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                          <span
                            style={{
                              fontSize: "10px",
                              color: "rgba(255, 255, 255, 0.8)",
                              fontWeight: 500,
                              textTransform: "uppercase",
                              letterSpacing: "0.5px"
                            }}
                          >
                            {currentSessionScenario.scenarioGrouping || "Scenario"}
                          </span>
                          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <span
                              style={{
                                fontSize: "14px",
                                color: "#fff",
                                fontWeight: 600
                              }}
                            >
                              {currentSessionScenario.scenarioName}
                            </span>
                            {currentSessionScenario.state &&
                              currentSessionScenario.state?.toUpperCase() === "PREVIEW" && (
                                <span
                                  style={{
                                    fontSize: "9px",
                                    color: "rgba(255, 255, 255, 0.7)",
                                    fontWeight: 500,
                                    padding: "2px 6px",
                                    background: "rgba(255, 255, 255, 0.2)",
                                    borderRadius: "4px",
                                    textTransform: "uppercase",
                                    letterSpacing: "0.3px"
                                  }}
                                >
                                  {currentSessionScenario.state}
                                </span>
                              )}
                          </div>
                        </div>
                      </div>
                    </TooltipHost>

                    {/* Action Buttons - Positioned on the right end, vertically centered */}
                    {currentSessionId && currentSessionId !== "" && (
                      <div
                        style={{
                          position: "absolute",
                          right: "20px",
                          top: "50%",
                          transform: "translateY(-50%)",
                          display: "flex",
                          gap: "2px"
                        }}
                      >
                        {/* Star Button - Only show for non-readonly sessions */}
                        {!isReadonlyMode && (
                          <TooltipHost
                            content={sessions.find(s => s.id === currentSessionId)?.isStarred ? "Remove from favorites" : "Add to favorites"}
                            directionalHint={DirectionalHint.bottomCenter}
                            calloutProps={{
                              gapSpace: 8,
                              directionalHintFixed: true
                            }}
                          >
                            <IconButton
                              iconProps={{ iconName: "FavoriteStarFill" }}
                              onClick={handleToggleStar}
                              disabled={isTogglingStar}
                              styles={{
                                root: {
                                  color: sessions.find(s => s.id === currentSessionId)?.isStarred ? "#FFB900" : "#605E5C",
                                  width: "36px",
                                  height: "36px"
                                },
                                rootHovered: {
                                  backgroundColor: sessions.find(s => s.id === currentSessionId)?.isStarred
                                    ? "rgba(255, 185, 0, 0.1)"
                                    : "rgba(96, 94, 92, 0.1)",
                                  color: sessions.find(s => s.id === currentSessionId)?.isStarred ? "#F59E00" : "#323130"
                                },
                                rootDisabled: {
                                  backgroundColor: "transparent",
                                  color: "#A19F9D"
                                },
                                icon: {
                                  fontSize: "18px"
                                }
                              }}
                            />
                          </TooltipHost>
                        )}

                        {/* Sample Prompts Button - Only show for non-readonly sessions */}
                        {currentSessionScenario && currentScenarioPrompts.length > 0 && !isReadonlyMode && (
                          <TooltipHost
                            content="View sample prompts"
                            directionalHint={DirectionalHint.bottomCenter}
                            calloutProps={{
                              gapSpace: 8,
                              directionalHintFixed: true
                            }}
                          >
                            <IconButton
                              iconProps={{ iconName: "Lightbulb" }}
                              onClick={() => {
                                logEvent("ViewSamplePromptsButton_Clicked", "AI Hub - Copilot Chat", "CopilotChat", {
                                  sessionId: currentSessionId,
                                  scenarioId: currentSessionScenario?.scenarioId,
                                  scenarioName: currentSessionScenario?.scenarioName
                                });
                                setIsPromptsPanelOpen(true);
                                setShowAllPrompts(false);
                              }}
                              styles={{
                                root: {
                                  color: "#0078D7",
                                  width: "36px",
                                  height: "36px"
                                },
                                rootHovered: {
                                  backgroundColor: "rgba(0, 120, 215, 0.1)",
                                  color: "#106ebe"
                                },
                                icon: {
                                  fontSize: "18px"
                                }
                              }}
                            />
                          </TooltipHost>
                        )}

                        {/* View Details Button */}
                        {currentSessionScenario && (
                          <TooltipHost
                            content="View scenario details"
                            directionalHint={DirectionalHint.bottomCenter}
                            calloutProps={{
                              gapSpace: 8,
                              directionalHintFixed: true
                            }}
                          >
                            <IconButton
                              iconProps={{ iconName: "Info" }}
                              onClick={() => {
                                logEvent("ViewScenarioDetailsButton_Clicked", "AI Hub - Copilot Chat", "CopilotChat", {
                                  sessionId: currentSessionId,
                                  scenarioId: currentSessionScenario?.scenarioId,
                                  scenarioName: currentSessionScenario?.scenarioName,
                                  calledFrom: "ChatScreen"
                                });
                                setIsDetailsPanelOpen(true);
                              }}
                              styles={{
                                root: {
                                  color: "#0078D7",
                                  width: "36px",
                                  height: "36px"
                                },
                                rootHovered: {
                                  backgroundColor: "rgba(0, 120, 215, 0.1)",
                                  color: "#106ebe"
                                },
                                icon: {
                                  fontSize: "18px"
                                }
                              }}
                            />
                          </TooltipHost>
                        )}

                        {/* Submit Feedback Button */}
                        {!isReadonlyMode && (
                          <div ref={feedbackButtonRef}>
                            <TooltipHost
                              content="Submit feedback"
                              directionalHint={DirectionalHint.bottomCenter}
                              calloutProps={{
                                gapSpace: 8,
                                directionalHintFixed: true
                              }}
                            >
                              <IconButton
                                iconProps={{ iconName: "Feedback" }}
                                onClick={() => {
                                  logEvent("SubmitFeedbackButton_Clicked", "AI Hub - Copilot Chat", "CopilotChat", {
                                    sessionId: currentSessionId,
                                    scenarioId: currentSessionScenario?.scenarioId,
                                    scenarioName: currentSessionScenario?.scenarioName
                                  });
                                  setIsFeedbackDialogOpen(true);
                                }}
                                styles={{
                                  root: {
                                    color: "#0078D7",
                                    width: "36px",
                                    height: "36px"
                                  },
                                  rootHovered: {
                                    backgroundColor: "rgba(0, 120, 215, 0.1)",
                                    color: "#106ebe"
                                  },
                                  icon: {
                                    fontSize: "18px"
                                  }
                                }}
                              />
                            </TooltipHost>
                          </div>
                        )}

                        {/* Share Session Button - Only show for non-readonly sessions */}
                        {!isReadonlyMode && (
                          <TooltipHost
                            content="Share this session"
                            directionalHint={DirectionalHint.bottomCenter}
                            calloutProps={{
                              gapSpace: 8,
                              directionalHintFixed: true
                            }}
                          >
                            <IconButton
                              iconProps={{ iconName: "Share" }}
                              onClick={() => {
                                logEvent("ShareChatButton_Clicked", "AI Hub - Copilot Chat", "CopilotChat", {
                                  sessionId: currentSessionId,
                                  scenarioId: currentSessionScenario?.scenarioId,
                                  scenarioName: currentSessionScenario?.scenarioName,
                                  calledFrom: "ChatScreen"
                                });
                                handleShareSession(currentSessionId);
                              }}
                              styles={{
                                root: {
                                  color: "#0078D7",
                                  width: "36px",
                                  height: "36px"
                                },
                                rootHovered: {
                                  backgroundColor: "rgba(0, 120, 215, 0.1)",
                                  color: "#106ebe"
                                },
                                icon: {
                                  fontSize: "18px"
                                }
                              }}
                            />
                          </TooltipHost>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {messages.map((msg, i) => (
                <ChatMessageComponent
                  key={i}
                  message={msg}
                  messageIndex={i}
                  expandedStates={expandedStates}
                  onToggleExpand={handleToggleExpand}
                  onShareChat={() => currentSessionId && handleShareSession(currentSessionId)}
                  onFeedback={(messageId, action, feedbackData) => handleMessageReaction(messageId, action, feedbackData)}
                  onRetry={msg.isError && msg.retryUserText ? () => handleRetry(msg.retryUserText!, i) : undefined}
                />
              ))}

              {loading && !messages.some((m) => m.isStreaming) && (
                <div style={{ display: "flex", marginBottom: "10px" }}>
                  <div
                    style={{
                      padding: "12px 16px",
                      borderRadius: "18px",
                      backgroundColor: "#e5e5ea",
                      display: "flex"
                    }}
                  >
                    <span style={{ ...dotStyle, animationDelay: "0s" }}></span>
                    <span style={{ ...dotStyle, animationDelay: "0.4s" }}></span>
                    <span style={{ ...dotStyle, animationDelay: "0.6s" }}></span>
                  </div>
                </div>
              )}
            </div>

            {!isReadonlyMode ? (
              <>
                <ChatInput
                  input={input}
                  loading={loading}
                  attachedFile={attachedFile}
                  attachedImages={attachedImages}
                  isRecording={isRecording}
                  isSpeechSupported={isSpeechSupported}
                  onInputChange={handleInputChange}
                  onKeyDown={handleKeyDown}
                  onFileChange={handleFileChange}
                  onImageChange={handleImageChange}
                  onRemoveFile={removeFile}
                  onRemoveImage={removeImageAt}
                  onSend={handleSend}
                  onStop={handleStop}
                  onStartRecording={startRecording}
                  onStopRecording={stopRecording}
                />
              </>
            ) : (
              <div
                style={{
                  width: "100%",
                  maxWidth: "100%",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  padding: "10px 0 20px 0",
                  flexShrink: 0 // Prevent continue button area from shrinking
                }}
              >
                <button
                  onClick={handleContinueChat}
                  disabled={isCopyingChat}
                  style={{
                    padding: "16px 48px",
                    background: isCopyingChat
                      ? "linear-gradient(135deg, #e0e0e0 0%, #d0d0d0 100%)"
                      : "linear-gradient(135deg, #0078D7 0%, #106ebe 100%)",
                    color: isCopyingChat ? "#999" : "#fff",
                    border: "none",
                    borderRadius: "12px",
                    cursor: isCopyingChat ? "not-allowed" : "pointer",
                    fontSize: "15px",
                    fontWeight: 600,
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    justifyContent: "center",
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    boxShadow: isCopyingChat ? "0 2px 4px rgba(0, 0, 0, 0.1)" : "0 6px 20px rgba(0, 120, 215, 0.4)"
                  }}
                  onMouseEnter={(e) => {
                    if (!isCopyingChat) {
                      e.currentTarget.style.transform = "translateY(-3px) scale(1.03)";
                      e.currentTarget.style.boxShadow = "0 10px 30px rgba(0, 120, 215, 0.5)";
                      e.currentTarget.style.background = "linear-gradient(135deg, #1084e8 0%, #0066b8 100%)";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isCopyingChat) {
                      e.currentTarget.style.transform = "translateY(0) scale(1)";
                      e.currentTarget.style.boxShadow = "0 6px 20px rgba(0, 120, 215, 0.4)";
                      e.currentTarget.style.background = "linear-gradient(135deg, #0078D7 0%, #106ebe 100%)";
                    }
                  }}
                >
                  <Icon iconName={isCopyingChat ? "Sync" : "Edit"} style={{ fontSize: "18px" }} />
                  <span style={{ letterSpacing: "0.3px" }}>{isCopyingChat ? "Copying Chat..." : "Continue Chat"}</span>
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {contextMenuProps && <ContextualMenu {...contextMenuProps} />}

      <Dialog
        hidden={!renameDialogOpen}
        onDismiss={() => setRenameDialogOpen(false)}
        dialogContentProps={{
          type: DialogType.normal,
          title: "Rename Chat",
          subText: "Enter new name for chat session."
        }}
      >
        <input
          type="text"
          value={renameInput}
          onChange={(e) => setRenameInput(e.target.value)}
          style={{ width: "100%", padding: "8px", marginBottom: "10px" }}
        />
        <DialogFooter>
          <PrimaryButton onClick={handleRename} text="Save" />
          <DefaultButton onClick={() => setRenameDialogOpen(false)} text="Cancel" />
        </DialogFooter>
      </Dialog>

      {/* Feedback Callout */}
      <FeedbackCallout
        targetElement={feedbackButtonRef.current}
        isOpen={isFeedbackDialogOpen}
        onDismiss={() => setIsFeedbackDialogOpen(false)}
        onSubmit={handleSubmitFeedback}
        isSubmitting={isSubmittingFeedback}
        isDisabled={!currentSessionId}
      />

      {/* Copy Toast Notification */}
      {showCopyToast && (
        <div
          style={{
            position: "fixed",
            top: "80px",
            right: "30px",
            background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
            color: "#fff",
            padding: "14px 24px",
            borderRadius: "12px",
            boxShadow: "0 8px 32px rgba(16, 185, 129, 0.4), 0 2px 8px rgba(0, 0, 0, 0.1)",
            display: "flex",
            alignItems: "center",
            gap: "12px",
            fontSize: "14px",
            fontWeight: 600,
            zIndex: 10000,
            animation: "slideInRight 0.3s ease-out",
            backdropFilter: "blur(8px)"
          }}
        >
          <Icon iconName="CheckMark" style={{ fontSize: "18px", color: "#fff" }} />
          <span>{successToastMessage}</span>
        </div>
      )}

      {/* Error Toast Notification */}
      {errorToast && (
        <div
          style={{
            position: "fixed",
            top: "80px",
            right: "30px",
            background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
            color: "#fff",
            padding: "14px 24px",
            borderRadius: "12px",
            boxShadow: "0 8px 32px rgba(239, 68, 68, 0.4), 0 2px 8px rgba(0, 0, 0, 0.1)",
            display: "flex",
            alignItems: "center",
            gap: "12px",
            fontSize: "14px",
            fontWeight: 600,
            zIndex: 10000,
            animation: "slideInRight 0.3s ease-out",
            backdropFilter: "blur(8px)",
            maxWidth: "400px"
          }}
        >
          <Icon iconName="ErrorBadge" style={{ fontSize: "18px", color: "#fff" }} />
          <span>{errorToast}</span>
        </div>
      )}

      {/* Scenario Details Panel */}
      {currentSessionScenario && (
        <ScenarioDetailsPanel
          isOpen={isDetailsPanelOpen}
          onDismiss={() => setIsDetailsPanelOpen(false)}
          scenarioName={currentSessionScenario.scenarioName}
          scenarioId={currentSessionScenario.scenarioId}
          scenarioDescription={
            scenarios.find((s) => s.scenarioId === currentSessionScenario.scenarioId)?.description || null
          }
          scenarioDetails={scenarioDetails}
          onStartChat={undefined}
          isSelected={false}
        />
      )}

      {/* Sample Prompts Panel */}
      {currentSessionScenario && currentScenarioPrompts.length > 0 && (
        <Panel
          isOpen={isPromptsPanelOpen}
          onDismiss={() => setIsPromptsPanelOpen(false)}
          type={PanelType.medium}
          headerText={
            currentSessionScenario.scenarioName
              ? `${currentSessionScenario.scenarioName} - Sample Prompts`
              : "Sample Prompts"
          }
          isLightDismiss
          styles={{
            main: {
              background: "#ffffff"
            },
            header: {
              borderBottom: "1px solid #edebe9"
            },
            headerText: {
              fontSize: "20px",
              fontWeight: 600,
              color: "#323130"
            },
            content: {
              padding: 0
            },
            scrollableContent: {
              height: "100%"
            }
          }}
        >
          <div style={{ padding: "24px" }}>
            {/* Scenario Info */}
            <div
              style={{
                padding: "16px",
                background: "linear-gradient(135deg, #e3f2fd 0%, #d1e7fd 100%)",
                borderRadius: "12px",
                marginBottom: "24px",
                border: "2px solid #0078D7"
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "8px" }}>
                <Icon iconName="Lightbulb" style={{ fontSize: "20px", color: "#0078D7" }} />
                <h3 style={{ margin: 0, fontSize: "16px", fontWeight: 600, color: "#106ebe" }}>
                  {currentSessionScenario.scenarioName}
                </h3>
              </div>
              <p style={{ margin: 0, fontSize: "13px", color: "#0078D7", lineHeight: "1.5" }}>
                Click on any prompt below to use it in your conversation
              </p>
            </div>

            {/* Prompts List */}
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "12px"
              }}
            >
              {(showAllPrompts ? currentScenarioPrompts : currentScenarioPrompts.slice(0, 6)).map(
                (prompt: string, index: number) => (
                  <div
                    key={index}
                    onClick={() => {
                      logEvent("SamplePromptFromScenario_Selected", "AI Hub - Copilot Chat", "CopilotChat", {
                        scenarioId: currentSessionScenario?.scenarioId,
                        scenarioName: currentSessionScenario?.scenarioName,
                        promptIndex: index,
                        promptText: prompt.substring(0, 100),
                        calledFrom: "ViewSamplePromptsPanel"
                      });
                      setInput(prompt);
                      setIsPromptsPanelOpen(false);
                      setShowAllPrompts(false);
                      setTimeout(() => {
                        if (textareaRef.current) {
                          textareaRef.current.style.height = "auto";
                          textareaRef.current.style.height = textareaRef.current.scrollHeight + "px";
                          textareaRef.current.focus();
                        }
                      }, 100);
                    }}
                    style={{
                      padding: "16px 18px",
                      background: "linear-gradient(135deg, #f8fbff 0%, #f0f7ff 100%)",
                      border: "2px solid #e1e7f5",
                      borderRadius: "12px",
                      cursor: "pointer",
                      transition: "all 0.3s ease",
                      position: "relative"
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "translateX(8px)";
                      e.currentTarget.style.borderColor = "#0078D7";
                      e.currentTarget.style.background = "#ffffff";
                      e.currentTarget.style.boxShadow = "0 6px 20px rgba(0, 120, 215, 0.25)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "translateX(0)";
                      e.currentTarget.style.borderColor = "#e1e7f5";
                      e.currentTarget.style.background = "linear-gradient(135deg, #f8fbff 0%, #f0f7ff 100%)";
                      e.currentTarget.style.boxShadow = "none";
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "flex-start", gap: "12px" }}>
                      <div
                        style={{
                          width: "32px",
                          height: "32px",
                          borderRadius: "8px",
                          background: "linear-gradient(135deg, #0078D7 0%, #106ebe 100%)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                          boxShadow: "0 2px 8px rgba(0, 120, 215, 0.3)"
                        }}
                      >
                        <Icon iconName="MessageFill" style={{ fontSize: "14px", color: "#ffffff" }} />
                      </div>
                      <p
                        style={{
                          margin: 0,
                          fontSize: "14px",
                          color: "#242424",
                          lineHeight: "1.6",
                          fontWeight: 500
                        }}
                      >
                        {prompt}
                      </p>
                    </div>
                  </div>
                )
              )}
            </div>

            {/* Show More/Less Button */}
            {currentScenarioPrompts.length > 6 && (
              <div style={{ marginTop: "20px", textAlign: "center" }}>
                <DefaultButton
                  text={showAllPrompts ? "Show Less" : `Show ${currentScenarioPrompts.length - 6} More`}
                  iconProps={{ iconName: showAllPrompts ? "ChevronUp" : "ChevronDown" }}
                  onClick={() => setShowAllPrompts(!showAllPrompts)}
                  styles={{
                    root: {
                      borderRadius: "8px",
                      fontSize: "13px",
                      fontWeight: 500,
                      border: "1px solid #0078D7",
                      background: "#ffffff",
                      color: "#0078D7",
                      padding: "10px 20px",
                      ":hover": {
                        background: "#f0f7ff"
                      }
                    }
                  }}
                />
              </div>
            )}
          </div>
        </Panel>
      )}

      <style>
        {`
                    @keyframes slideInRight {
                        0% {
                            opacity: 0;
                            transform: translateX(100px);
                        }
                        100% {
                            opacity: 1;
                            transform: translateX(0);
                        }
                    }
                    @keyframes slideUpFade {
                        0% {
                            opacity: 0;
                            transform: translateY(20px);
                        }
                        100% {
                            opacity: 1;
                            transform: translateY(0);
                        }
                    }
                `}
      </style>
    </div>
  );
};

export default CopilotChat;
